import { ArrowLeft, BookText, RotateCcw, Highlighter, X, StickyNote as StickyNoteIcon, BookOpen, Loader2, FileText, Edit, ChevronLeft, ChevronRight, Bookmark, NotebookText, Volume2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Card, CardContent } from '@/components/ui/card';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ActivityModal from './ActivityModal';
import StickyNote from './StickyNote';
import PDFSectionViewer from './PDFSectionViewer';
import LearningPackViewer from './LearningPackViewer';
import BookmarksView from './BookmarksView';
import BookmarkIcon from './BookmarkIcon';
import BudleeAIIcon from './BudleeAIIcon';
import { useLearningPack } from '@/hooks/useLearningPack';
import { useHighlights } from '@/hooks/useHighlights';
// Removed useStickyNotes import - using backend notes exclusively
import { toast } from 'sonner';
import { getAuthToken, getPortalType } from '@/utils/auth';
import { shortNotesAPI } from '@/api/shortNotes';
import { userNotesAPI, getStudentTeacherNotes } from '@/api/userNotes';
import { chapterAnnotationsAPI } from '@/api/chapterAnnotations';
import { userAnnotationPositionsAPI } from '@/api/userAnnotationPositions';
import { userActivitiesAPI } from '@/api/userActivities';
import { bookmarksAPI } from '@/api/bookmarks';
import { apiUrl } from '@/api/config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkLatexSanitizer from '@/utils/remarkLatexSanitizer';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import { sanitizeChatMarkdown } from '@/utils/chatMarkdownSanitizer';

/**
 * Parse a Short Notes point into structured components
 * Extracts: term, definition, examples, and formulas
 * 
 * Expected format:
 * **Term**: Definition text.
 * - **Example**: Example text
 * - **Formula**: $LaTeX$ formula
 */
const parseShortNotePoint = (point) => {
  if (!point) return null;

  let term = null;
  let definition = point;
  let examples = [];
  let formulas = [];
  let hasStructure = false;

  // 1. Try strict bold format: **Term**: Definition
  let termMatch = point.match(/^\*\*(.*?)\*\*:\s*(.*?)(?:\n|$)/s);

  // 2. Try loose format: Term: Definition (if Term is short and no bold)
  // Avoid capturing "Example:" or "Formula:" as terms
  if (!termMatch) {
    const looseMatch = point.match(/^([^:\n]{1,50}):\s*(.*?)(?:\n|$)/s);
    if (looseMatch) {
      const candidateTerm = looseMatch[1].trim();
      if (!['example', 'formula', 'note'].includes(candidateTerm.toLowerCase())) {
        termMatch = looseMatch;
      }
    }
  }

  if (termMatch) {
    term = termMatch[1].trim();
    definition = termMatch[2].trim() + (point.substring(termMatch[0].length) || '');
    hasStructure = true;
  }

  // 3. Handle content starting directly with "Example:"
  if (!hasStructure && point.trim().match(/^Example:\s*/i)) {
    examples.push(point.replace(/^Example:\s*/i, '').trim());
    return { term: "Example", definition: null, examples, formulas: [], hasStructure: true };
  }

  // 4. Extract Examples (both bulleted and inline legacy "Example: ...")
  // strict bullet
  const strictExampleMatches = Array.from(point.matchAll(/-\s*\*\*Example\*\*:\s*(.*?)(?=\n|$)/gs));
  strictExampleMatches.forEach(m => examples.push(m[1].trim()));

  // legacy inline "Example: ..." (if not already caught)
  // We only look for this if we haven't found strict examples to avoid duplication
  if (examples.length === 0) {
    const legacyExampleMatch = point.match(/(?:^|[\s.])Example:\s*(.*?)(?=\n|$)/i);
    // Only use if it's not at the very start (unless we didn't find a term, but that's handled in step 3)
    if (legacyExampleMatch && legacyExampleMatch.index > 0) {
      examples.push(legacyExampleMatch[1].trim());
      // Remove it from definition to clean up
      definition = definition.replace(legacyExampleMatch[0], '').trim();
      if (definition.endsWith('.')) definition = definition.slice(0, -1);
    }
  }

  // 5. Extract Formulas
  const strictFormulaMatches = Array.from(point.matchAll(/-\s*\*\*Formula\*\*:\s*(.*?)(?=\n|$)/gs));
  strictFormulaMatches.forEach(m => formulas.push(m[1].trim()));

  // legacy inline "Formula: ..."
  if (formulas.length === 0) {
    const legacyFormulaMatch = point.match(/(?:^|[\s.])Formula:\s*(.*?)(?=\n|$)/i);
    if (legacyFormulaMatch) {
      formulas.push(legacyFormulaMatch[1].trim());
      definition = definition.replace(legacyFormulaMatch[0], '').trim();
      if (definition.endsWith('.')) definition = definition.slice(0, -1);
    }
  }

  // Clean up definition if term was found (remove the term part)
  // Actually termMatch logic already separated it roughly, but 'definition' var might have extra text from newlines if we appended substring.
  // Let's rely on the structure:
  // If we found strict bullets, we should remove them from definition.
  if (hasStructure) {
    definition = definition
      .replace(/-\s*\*\*Example\*\*:\s*(.*?)(?=\n|$)/g, '')
      .replace(/-\s*\*\*Formula\*\*:\s*(.*?)(?=\n|$)/g, '')
      .trim();
  }

  return {
    term,
    definition,
    examples,
    formulas,
    hasStructure: hasStructure || examples.length > 0,
    raw: point
  };
};

const processNotePoints = (points, contextTitle = "Introduction") => {
  if (!points || !Array.isArray(points)) return [];

  const merged = [];

  points.forEach((rawPoint) => {
    const note = parseShortNotePoint(rawPoint);
    if (!note) return;

    // Check if orphan "Example" (either intentional or accidental generation)
    // Condition: term is "Example", or term is empty but has Example content
    // We also handle cases where legacy parser matched "Example" as term
    const isOrphan = note.term === 'Example' || (!note.term && (note.examples.length > 0 || note.formulas.length > 0));

    // If it's an orphan and we have a previous note to merge into
    if (isOrphan && merged.length > 0) {
      const prev = merged[merged.length - 1];

      // Merge arrays
      if (note.examples) prev.examples.push(...note.examples);
      if (note.formulas) prev.formulas.push(...note.formulas);

      // Append definition if it exists and isn't just a duplicate of examples
      // (Our parser might have put the example text into definition if it fell through)
      if (note.definition) {
        prev.definition = prev.definition ? `${prev.definition} ${note.definition}` : note.definition;
      }

      // Merge raw content for fallback completeness
      prev.raw = (prev.raw || '') + '\n\n' + (note.raw || '');

      return; // Skip adding this note as a separate card
    }

    // If it's the FIRST note and is an orphan "Example", rename it to be presentable
    if (isOrphan && merged.length === 0) {
      note.term = contextTitle;
    }

    merged.push(note);
  });

  return merged;
};

// Helper function to clean markdown formatting (moved outside component)
const cleanMarkdown = (text) => {
  if (!text) return text || '';

  // Protect URLs by temporarily replacing them with placeholders
  const urlPlaceholders = [];
  // Match URLs (http://, https://, or www. or domain patterns)
  const urlPattern = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}[^\s<>"{}|\\^`\[\]]*)/g;
  let placeholderIndex = 0;

  // Replace URLs with placeholders (using format that won't conflict with markdown)
  let cleaned = text.replace(urlPattern, (url) => {
    const placeholder = `{{URLPLACEHOLDER${placeholderIndex}}}`;
    urlPlaceholders[placeholderIndex] = url;
    placeholderIndex++;
    return placeholder;
  });

  // Remove markdown bold (**text** or __text__)
  cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '$1');
  cleaned = cleaned.replace(/__(.+?)__/g, '$1');
  // Remove markdown italic (*text* or _text_)
  cleaned = cleaned.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '$1');
  // Remove markdown italic _text_ (but not if it's part of a URL placeholder)
  // Only match _text_ where text doesn't contain URL placeholder markers
  cleaned = cleaned.replace(/(?<!_)_(?!_)(?![^_]*URLPLACEHOLDER)(.+?)(?<!_)_(?!_)/g, '$1');
  // Remove markdown code (`text`)
  cleaned = cleaned.replace(/`(.+?)`/g, '$1');
  // Remove markdown links [text](url) -> text
  cleaned = cleaned.replace(/\[(.+?)\]\(.+?\)/g, '$1');
  // Remove markdown headers (# ## ###)
  cleaned = cleaned.replace(/^#{1,6}\s+/gm, '');

  // Restore URLs from placeholders
  urlPlaceholders.forEach((url, index) => {
    cleaned = cleaned.replace(`{{URLPLACEHOLDER${index}}}`, url);
  });

  return cleaned.trim();
};

// Helper to normalize titles (Defensive fix for ReferenceError)
const normalizeTitle = (title) => title || '';

export default function TextbookViewer({
  lesson,
  onClose,
  onXPEarned,
  onAskBudleeAI,
  onToggleChatMode,
  chatMode = false,
  onStartJourney,
  journeyMode = false,
  chapterId = null,
  refreshKey = 0,
  hideBackButton = false,
  citationHighlights = [],
  citationHighlightedHighlightId = null,
  pdfFullBleed = false,
  pdfInitialPage = 1,
  fillParentHeight = false,
  // Mobile layout control (optional)
  showPdf = true,
  isMobileSplit = false,
  isFullScreenJourney = false,
  onTogglePdf = null,
  onRequestFullJourney = null,
  targetSection = null,
  targetAnnotationId = null,
  activeAnnotationId = null,
  onClearTargetAnnotation = null,
  onClearActiveAnnotation = null,
  onSectionScroll = null, // Callback when PDF scrolls to new section
}) {
  // State declarations - must come before useEffect hooks that use them
  const [showNotes, setShowNotes] = useState(false); // Short Notes view
  const [showClassNotes, setShowClassNotes] = useState(false); // Class Notes view
  const [showMyNotes, setShowMyNotes] = useState(false); // My Notes view
  const [showMyHighlights, setShowMyHighlights] = useState(false); // My Highlights view
  const [showBookmarks, setShowBookmarks] = useState(false); // Bookmarks view
  const [bookmarksTab, setBookmarksTab] = useState('learning_pack'); // BookmarksView tab state

  // Treat the PDF as a persistent view and hide it when other tabs are active.
  // This avoids unmount/remount (and re-fetch/re-parse) when switching tabs.
  const isPdfViewActive = !showBookmarks && !showMyNotes && !showMyHighlights && !showNotes && !showClassNotes;

  // Bookmarks refresh trigger
  const [bookmarksRefreshTrigger, setBookmarksRefreshTrigger] = useState(0);

  // Reset BookmarksView tab when switching to Bookmarks
  useEffect(() => {
    if (showBookmarks) {
      setBookmarksTab('learning_pack');
    }
  }, [showBookmarks]);
  const [notesContent, setNotesContent] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [notesError, setNotesError] = useState(null);

  // Class Notes state
  const [classNotes, setClassNotes] = useState([]);
  const [loadingClassNotes, setLoadingClassNotes] = useState(false);
  const [activeClassNoteIndex, setActiveClassNoteIndex] = useState(0); // Track which note is currently visible

  // Fetch Class Notes (Teacher's Notes)
  useEffect(() => {
    const fetchClassNotes = async () => {
      if (!chapterId) {
        setClassNotes([]);
        return;
      }

      setLoadingClassNotes(true);
      try {
        const notes = await getStudentTeacherNotes(chapterId);
        if (Array.isArray(notes)) {
          console.log('📚 Loaded Class Notes:', notes.length);
          setClassNotes(notes);
        } else {
          setClassNotes([]);
        }
      } catch (error) {
        console.error('Error fetching class notes:', error);
        setClassNotes([]);
      } finally {
        setLoadingClassNotes(false);
      }
    };

    fetchClassNotes();
  }, [chapterId]);

  // User Notes state
  const [userNotes, setUserNotes] = useState([]);
  const [loadingUserNotes, setLoadingUserNotes] = useState(false);
  // notePlacementMode removed - notes now created directly

  // Learning Pack navigation state
  const [currentLearningPackIndex, setCurrentLearningPackIndex] = useState(0);
  const [learningPackItems, setLearningPackItems] = useState([]);
  const [learningPackSectionNumber, setLearningPackSectionNumber] = useState(null);
  const [learningPackSectionContext, setLearningPackSectionContext] = useState(null);
  const [editingNoteForPDF, setEditingNoteForPDF] = useState(null); // Note to auto-open in PDF viewer

  // Chapter sections (used for robust section sync + fallback section inference for older annotations)
  const [chapterSectionsData, setChapterSectionsData] = useState({ sections: [], numPages: null, status: null });

  // Chapter Annotations state (icons placed by superadmins)
  const [chapterAnnotations, setChapterAnnotations] = useState([]);
  const [loadingAnnotations, setLoadingAnnotations] = useState(false);

  // Chapter annotations can briefly reset during navigation / reloads.
  // Cache the last successful payload per chapterId to prevent UI flicker.
  const chapterAnnotationsCacheRef = useRef(new Map());
  const lastStableChapterIdRef = useRef(null);
  useEffect(() => {
    if (chapterId) lastStableChapterIdRef.current = chapterId;
  }, [chapterId]);

  // User Annotation Positions state (per-user custom positions)
  const [userAnnotationPositions, setUserAnnotationPositions] = useState({});

  // Backend activity completions state (source of truth)
  const [backendCompletions, setBackendCompletions] = useState({});

  // Active Bookmarks Map state (for syncing PDF viewer icons)
  const [activeBookmarks, setActiveBookmarks] = useState({});

  // Fetch active bookmarks for the current chapter
  const fetchActiveBookmarks = useCallback(async () => {
    if (!chapterId) return;
    try {
      const bookmarks = await bookmarksAPI.getChapterBookmarks(chapterId);
      if (Array.isArray(bookmarks)) {
        // Create a map of contentId -> bookmarkId
        const bookmarksMap = {};
        bookmarks.forEach(b => {
          if ((b.bookmark_type === 'activity' || b.bookmark_type === 'learning_pack') && b.content_id) {
            bookmarksMap[String(b.content_id)] = b.id;
          }
        });
        setActiveBookmarks(bookmarksMap);
      }
    } catch (error) {
      console.error('Error fetching active bookmarks:', error);
    }
  }, [chapterId]);

  // Fetch active bookmarks on mount and when chapter changes or refresh trigger fires
  useEffect(() => {
    fetchActiveBookmarks();
  }, [fetchActiveBookmarks, bookmarksRefreshTrigger]);

  // Debug: Log lesson data to see what we're receiving
  useEffect(() => {
    console.log('📚 TextbookViewer component state:', {
      hasLesson: !!lesson,
      hasPdfUrl: !!lesson?.pdfUrl,
      pdfUrl: lesson?.pdfUrl,
      hasTextbookImage: !!lesson?.textbookImage,
      textbookImage: lesson?.textbookImage,
      title: lesson?.title,
      chapterId: chapterId,
      classNotesCount: classNotes.length,
      loadingClassNotes: loadingClassNotes,
      refreshKey: refreshKey
    });

    if (lesson) {
      console.log('📚 TextbookViewer received lesson:', {
        hasPdfUrl: !!lesson.pdfUrl,
        pdfUrl: lesson.pdfUrl,
        hasTextbookImage: !!lesson.textbookImage,
        textbookImage: lesson.textbookImage,
        title: lesson.title,
        chapterId: chapterId,
      });
    }
  }, [lesson, chapterId, classNotes.length, loadingClassNotes, refreshKey]);

  // Reset notes view when chapter changes
  useEffect(() => {
    if (chapterId) {
      setShowNotes(false);
      setShowMyNotes(false);
      setShowMyHighlights(false);
      setShowBookmarks(false);
    }
  }, [chapterId]);

  // Load user notes from backend
  const loadUserNotes = useCallback(async () => {
    if (!chapterId) {
      console.warn('⚠️ Cannot load notes: chapterId is missing');
      return;
    }

    console.log('🔄 Loading notes for chapter:', chapterId);
    console.log('🔄 Making API call to:', `/api/users/notes/?chapter_id=${chapterId}`);
    setLoadingUserNotes(true);
    try {
      const data = await userNotesAPI.getChapterNotes(chapterId);
      console.log('📝 Raw notes data from API:', data);
      console.log('📝 Data type:', typeof data, 'Is array:', Array.isArray(data), 'Length:', data?.length);

      // Safety check: ensure data is an array
      if (!data || !Array.isArray(data)) {
        console.warn('⚠️ Notes data is not an array:', data);
        setUserNotes([]);
        return;
      }

      // Normalize position for all notes
      // For "My Notes" view, we show ALL notes (even without positions)
      // For PDF display, we'll filter notes with valid positions separately
      const normalizedNotes = data.map(note => {
        // Normalize position - backend returns position as {x, y} or position_x/position_y
        let position = null;

        if (note.position && typeof note.position === 'object') {
          // Check if position has valid (non-null) x and y values
          if (note.position.x != null && note.position.y != null) {
            position = { x: note.position.x, y: note.position.y };
          }
        } else if (note.position_x != null && note.position_y != null) {
          // Position is in separate fields
          position = { x: note.position_x, y: note.position_y };
        }

        return {
          ...note,
          position: position
        };
      });

      console.log('📝 Loaded notes:', normalizedNotes.length, 'total notes');
      setUserNotes(normalizedNotes);
    } catch (error) {
      console.error('Error loading user notes:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view notes');
      } else if (error.response?.status === 404) {
        // No notes found is not an error
        setUserNotes([]);
      } else {
        toast.error('Failed to load notes');
      }
    } finally {
      setLoadingUserNotes(false);
    }
  }, [chapterId]);

  // Fetch user notes when chapter changes, PDF viewer is shown, My Notes view is opened, or note placement mode is active
  useEffect(() => {
    if (!chapterId) return;

    // Always load when My Notes view is opened
    if (showMyNotes) {
      loadUserNotes();
      return;
    }

    // Auto-load user notes when opening Learn page
    if (chapterId && lesson?.pdfUrl) {
      loadUserNotes();
    }
  }, [chapterId, lesson?.pdfUrl, showNotes, showMyNotes, loadUserNotes]);

  // Load chapter annotations when chapter changes
  const loadChapterAnnotations = async () => {
    if (!chapterId) return;

    setLoadingAnnotations(true);
    try {
      const annotations = await chapterAnnotationsAPI.getChapterAnnotations(chapterId);
      console.log('📌 Loaded chapter annotations:', annotations);
      setChapterAnnotations(annotations);
      chapterAnnotationsCacheRef.current.set(String(chapterId), Array.isArray(annotations) ? annotations : []);
    } catch (error) {
      console.error('Error loading chapter annotations:', error);
      // Don't show error toast, just log it (annotations are optional)
      // Keep existing/cached annotations to avoid UI flicker.
    } finally {
      setLoadingAnnotations(false);
    }
  };

  // Load annotations when chapter changes
  useEffect(() => {
    loadChapterAnnotations();
  }, [chapterId]);

  const stableChapterId = chapterId || lastStableChapterIdRef.current;
  const cachedChapterAnnotations = stableChapterId
    ? (chapterAnnotationsCacheRef.current.get(String(stableChapterId)) || [])
    : [];
  const effectiveChapterAnnotations =
    Array.isArray(chapterAnnotations) && chapterAnnotations.length > 0
      ? chapterAnnotations
      : cachedChapterAnnotations;

  // DEBUG: Log annotations to check duration data
  useEffect(() => {
    if (effectiveChapterAnnotations.length > 0) {
      console.log('🔍 Effective Chapter Annotations Debug:', effectiveChapterAnnotations.map(a => ({
        id: a.id,
        type: a.annotation_type,
        title: a.title,
        duration: a.duration,
        duration_seconds: a.duration_seconds,
        has_video_file: !!a.video_file,
        has_watch_content: !!a.watch_content
      })));
    }
  }, [effectiveChapterAnnotations]);

  const loadChapterSections = useCallback(async () => {
    if (!chapterId) {
      setChapterSectionsData({ sections: [], numPages: null, status: null });
      return;
    }

    try {
      const authToken = getAuthToken(getPortalType(window.location.pathname || ''));
      const response = await fetch(apiUrl(`userportal/superadmin/chapters/${chapterId}/sections/`), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        cache: 'default',
      });

      if (!response.ok) {
        setChapterSectionsData({ sections: [], numPages: null, status: 'error' });
        return;
      }

      const data = await response.json();
      setChapterSectionsData({
        sections: Array.isArray(data?.sections) ? data.sections : [],
        numPages: data?.numPages ?? null,
        status: data?.status ?? null,
      });
    } catch (e) {
      setChapterSectionsData({ sections: [], numPages: null, status: 'error' });
    }
  }, [chapterId]);

  useEffect(() => {
    loadChapterSections();
  }, [loadChapterSections]);

  const isLearningPackAnnotation = useCallback((ann) => {
    const raw = String(ann?.annotation_type ?? '').trim();
    if (!raw) return false;
    const normalized = raw.toLowerCase();
    return (
      normalized === 'watch' ||
      normalized === 'video' ||
      normalized === 'explore' ||
      normalized === 'practice' ||
      normalized === 'curiosity' ||
      normalized === 'basicconcepts' ||
      normalized === 'basic concepts'
    );
  }, []);

  const inferSectionNumberFromPage = useCallback((pageNumber) => {
    const pageNum = Math.max(1, parseInt(pageNumber, 10) || 1);
    const sections = chapterSectionsData?.sections;
    if (!Array.isArray(sections) || sections.length === 0) return null;

    let chosen = null;
    for (const section of sections) {
      const sectionPage = section?.pageNumber;
      if (!sectionPage) continue;
      if (sectionPage <= pageNum) {
        chosen = section?.sectionNumber ?? null;
      } else {
        break;
      }
    }
    return chosen;
  }, [chapterSectionsData]);

  // Load user annotation positions for the chapter
  const loadAnnotationPositions = async () => {
    if (!chapterId) {
      setUserAnnotationPositions({});
      return;
    }

    try {
      const positions = await userAnnotationPositionsAPI.getChapterPositions(chapterId);
      setUserAnnotationPositions(positions || {});
    } catch (error) {
      console.error('Error loading annotation positions:', error);
      // Don't show error toast, just log it (positions are optional)
      setUserAnnotationPositions({});
    }
  };

  // Load annotation positions when chapter changes
  useEffect(() => {
    loadAnnotationPositions();
  }, [chapterId]);

  // Load activity completions from backend when chapter changes
  const loadActivityCompletions = async () => {
    if (!chapterId) {
      setBackendCompletions({});
      return;
    }

    try {
      const completions = await userActivitiesAPI.getChapterCompletions(chapterId);
      console.log('📊 Loaded activity completions from backend in TextbookViewer:', completions);

      // Create a map of annotation_id -> completion data
      const completionsMap = {};
      completions.forEach(completion => {
        const annotationId = completion.annotation_id || completion.annotation?.id;
        if (annotationId) {
          completionsMap[String(annotationId)] = {
            completedAt: completion.completed_at || new Date().toISOString(),
            xpEarned: completion.xp_earned || 0,
            gemsEarned: completion.gems_earned || 0
          };
        }
      });

      setBackendCompletions(completionsMap);

      // Also sync to localStorage for useLearningPack
      const packId = chapterId ? `chapter_${chapterId}` : 'default';
      const storageKey = `learningPack_${packId}_progress`;
      const existingLocalData = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const mergedCompletions = {
        ...existingLocalData,
        ...completionsMap // Backend data overwrites local data
      };

      if (Object.keys(mergedCompletions).length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(mergedCompletions));
      }

      // Force reload packState from localStorage
      packState.reloadFromStorage();

      console.log('✅ Synced backend completions in TextbookViewer');
    } catch (error) {
      console.error('Error loading activity completions from backend:', error);
      // Don't show error toast - just log it (backend might be unavailable)
    }
  };

  // Load completions when chapter changes
  useEffect(() => {
    loadActivityCompletions();
  }, [chapterId]);

  // Listen for activity completion events to reload completions
  useEffect(() => {
    const handleActivityCompletionSaved = (event) => {
      if (event.detail?.chapter_id === chapterId) {
        console.log('🔄 Activity completion saved, reloading completions...');
        loadActivityCompletions();
      }
    };

    window.addEventListener('activity-completion-saved', handleActivityCompletionSaved);
    return () => {
      window.removeEventListener('activity-completion-saved', handleActivityCompletionSaved);
    };
  }, [chapterId]);

  // Note: handlePDFClick removed - notes are now created directly via handleAddNoteClick

  // Create note at specific position
  const createNoteAtPosition = async (pageNumber, position) => {
    if (!chapterId) return;

    // Optimistic update - create temporary note immediately
    const tempNoteId = `temp-${Date.now()}`;
    // Ensure page_number is a number
    const pageNum = typeof pageNumber === 'string' ? parseInt(pageNumber, 10) : pageNumber;
    const optimisticNote = {
      id: tempNoteId,
      chapter_id: chapterId,
      heading: '',
      content: '',
      page_number: pageNum, // Ensure it's a number
      position: { x: position.x, y: position.y },
      color: 'yellow',
      is_pinned: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      chapter: { id: chapterId, chapter_name: '', order: 0 }
    };

    // Add optimistic note to state immediately
    // Use functional update to ensure React detects the change
    setUserNotes(prev => {
      // Create a completely new array to ensure React detects the change
      const newNotes = [optimisticNote, ...prev];
      console.log('📝 Adding optimistic note:', {
        tempId: tempNoteId,
        pageNumber: pageNum,
        position: optimisticNote.position,
        totalNotes: newNotes.length,
        prevNotesCount: prev.length
      });
      return newNotes;
    });

    try {
      const newNote = await userNotesAPI.create({
        chapter_id: chapterId,
        heading: '',
        content: '',
        page_number: pageNum,
        position: {
          x: position.x,
          y: position.y
        },
        color: 'yellow'
      });

      console.log('📝 Backend response (newNote):', {
        newNote: newNote,
        hasId: !!newNote.id,
        id: newNote.id,
        hasPosition: !!newNote.position,
        position: newNote.position,
        hasPositionX: newNote.position_x !== undefined,
        position_x: newNote.position_x,
        position_y: newNote.position_y,
        keys: Object.keys(newNote)
      });

      // Replace optimistic note with real note
      // Ensure position is normalized correctly
      let normalizedPosition = null;
      if (newNote.position && typeof newNote.position === 'object' && newNote.position.x !== undefined && newNote.position.y !== undefined) {
        normalizedPosition = { x: newNote.position.x, y: newNote.position.y };
      } else if (newNote.position_x !== undefined && newNote.position_y !== undefined) {
        normalizedPosition = { x: newNote.position_x, y: newNote.position_y };
      }

      if (!normalizedPosition) {
        console.error('⚠️ New note missing position data:', newNote);
        // Use the optimistic note's position as fallback
        normalizedPosition = optimisticNote.position;
      }

      // Ensure we have an ID - check multiple possible locations
      const noteId = newNote.id || newNote.pk || null;
      if (!noteId) {
        console.error('❌ Note missing ID, cannot proceed:', {
          newNote: newNote,
          keys: Object.keys(newNote),
          hasId: !!newNote.id,
          hasPk: !!newNote.pk
        });
        toast.error('Note created but missing ID');
        return;
      }

      const noteWithPosition = {
        ...newNote,
        id: noteId, // Ensure ID is set
        position: normalizedPosition
      };

      console.log('📝 Replacing temp note with real note:', {
        tempId: tempNoteId,
        realId: noteWithPosition.id,
        hasPosition: !!noteWithPosition.position,
        position: noteWithPosition.position,
        fullNote: noteWithPosition
      });

      setUserNotes(prev => {
        // Remove temp note and add real note - create new array for React
        const filtered = prev.filter(note => note.id !== tempNoteId);
        const newNotes = [noteWithPosition, ...filtered];
        console.log('📝 Updated notes array:', {
          removed: prev.length - filtered.length,
          added: 1,
          total: newNotes.length
        });
        return newNotes;
      });

      // Auto-open edit modal for the new note (only after real note is created)
      // Use a two-step approach: clear first, then set with new object reference
      setEditingNoteForPDF(null);

      // Use multiple timeouts to ensure state updates are processed in order
      // and the PDF has time to render the new note
      setTimeout(() => {
        // Create a completely fresh object with timestamp to force new reference
        // This ensures React detects the change even if the note data is similar
        const noteToEdit = {
          ...noteWithPosition,
          _timestamp: Date.now(), // Add timestamp to force new reference
          _autoOpen: true // Flag to indicate this should auto-open
        };
        console.log('📝 Setting note to auto-edit:', {
          id: noteToEdit.id,
          idType: typeof noteToEdit.id,
          page: noteToEdit.page_number,
          hasPosition: !!noteToEdit.position,
          timestamp: noteToEdit._timestamp,
          fullNote: noteToEdit
        });
        setEditingNoteForPDF(noteToEdit);
      }, 300); // Increased delay to ensure note is in state and PDF has rendered

      toast.success('Note created!');
    } catch (error) {
      console.error('Error creating note:', error);
      // Remove optimistic note on error
      setUserNotes(prev => prev.filter(note => note.id !== tempNoteId));
      toast.error('Failed to create note');
    }
  };

  // Handle Add Note button click
  const handleAddNoteClick = () => {
    if (!chapterId) {
      toast.error('Please select a chapter first');
      return;
    }

    if (!lesson?.pdfUrl) {
      toast.error('PDF not available');
      return;
    }

    // Create note directly on current page (or page 1 if not available)
    // Default position: center of page
    const defaultPageNumber = 1; // TODO: Get actual current visible page from PDF viewer
    const defaultPosition = { x: 200, y: 100 }; // Default centered position

    createNoteAtPosition(defaultPageNumber, defaultPosition);

    // Close other panels
    setShowMyNotes(false);
    setShowNotes(false);
  };

  // Handle My Notes button click
  const handleMyNotesClick = () => {
    const willShowMyNotes = !showMyNotes;
    setShowMyNotes(willShowMyNotes);
    setShowNotes(false);
    setShowMyHighlights(false);
    setShowBookmarks(false);

    // Always reload notes when opening My Notes view
    if (willShowMyNotes && chapterId) {
      loadUserNotes();
    }
  };

  const handleBookmarksClick = () => {
    setShowBookmarks(!showBookmarks);
    setShowNotes(false);
    setShowMyNotes(false);
    setShowMyHighlights(false);
  };

  const handleBookmarkNavigation = (bookmark) => {
    // 1. If it's an activity/video, find annotation and open it
    if (bookmark.bookmark_type === 'activity' || bookmark.bookmark_type === 'learning_pack') {
      if (bookmark.content_id) {
        const annotation = effectiveChapterAnnotations.find(a => String(a.id) === String(bookmark.content_id));
        if (annotation) {
          console.log("Navigating to annotation:", annotation);
          // Logic to trigger activity modal would go here. 
          // Ideally we pass `onNavigate` up or expose a method.
        }
      }
    }

    if (bookmark.section_number) {
      console.log("Navigating to bookmark section:", bookmark.section_number);
      // Simple visual feedback only for now as deep-linking inside PDF requires more refactoring
    }
  };

  // Handle My Highlights button click
  const handleMyHighlightsClick = () => {
    const willShowMyHighlights = !showMyHighlights;
    setShowMyHighlights(willShowMyHighlights);
    setShowNotes(false);
    setShowMyNotes(false);
    setShowBookmarks(false);

    // Always reload highlights when opening My Highlights view
    if (willShowMyHighlights && chapterId && reloadHighlights) {
      reloadHighlights();
    }
  };

  // Update note
  const handleUpdateNote = async (noteId, updates) => {
    try {
      // Check if this is a temp note
      if (noteId && noteId.toString().startsWith('temp-')) {
        console.warn('Cannot update temp note:', noteId);
        throw new Error('Note is still being created. Please wait a moment and try again.');
      }

      const updatedNote = await userNotesAPI.update(noteId, updates);
      // Ensure position is normalized correctly
      let position = null;
      if (updatedNote.position && typeof updatedNote.position === 'object' && updatedNote.position.x !== undefined && updatedNote.position.y !== undefined) {
        position = { x: updatedNote.position.x, y: updatedNote.position.y };
      } else if (updatedNote.position_x !== undefined && updatedNote.position_y !== undefined) {
        position = { x: updatedNote.position_x, y: updatedNote.position_y };
      }

      // If position is missing, try to get it from the existing note
      if (!position) {
        const existingNote = userNotes.find(n => n.id === noteId);
        if (existingNote && existingNote.position) {
          position = existingNote.position;
        }
      }

      const noteWithPosition = {
        ...updatedNote,
        position: position
      };

      // Update local state immediately for instant UI feedback
      setUserNotes(prev => prev.map(note => note.id === noteId ? noteWithPosition : note));

      // If position was updated, also reload notes from backend after a short delay
      // This ensures the position is properly persisted and will be correct on page reload
      if (updates.position_x !== undefined || updates.position_y !== undefined) {
        // Reload notes after a short delay to ensure backend has processed the update
        // This is important for persistence across page reloads and chapter switches
        setTimeout(() => {
          loadUserNotes();
        }, 500); // Slightly longer delay to ensure backend has saved
      }

      toast.success('Note updated');
      return updatedNote;
    } catch (error) {
      console.error('Error updating note:', error);
      // Log detailed error information
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Request payload:', updates);
      }
      const errorMessage = error.response?.data?.error ||
        error.response?.data?.detail ||
        (typeof error.response?.data === 'object' ? JSON.stringify(error.response.data) : '') ||
        error.message ||
        'Failed to update note';
      toast.error(errorMessage);
      throw error; // Re-throw so modal can handle it
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    try {
      await userNotesAPI.delete(noteId);
      setUserNotes(prev => prev.filter(note => note.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      console.error('Error deleting note:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || error.message || 'Failed to delete note';
      toast.error(errorMessage);
      throw error; // Re-throw so modal can handle it
    }
  };

  // Handle note page change when dragged across pages
  const handleNotePageChange = async (noteId, newPageNumber) => {
    try {
      // Optimistically update local state
      setUserNotes(prev => prev.map(note =>
        note.id === noteId ? { ...note, page_number: newPageNumber } : note
      ));

      // Update backend
      await userNotesAPI.update(noteId, { page_number: newPageNumber });

      console.log('📝 Note page updated:', { noteId, newPageNumber });
    } catch (error) {
      console.error('Failed to update note page:', error);
      toast.error('Failed to update note page');
      // Reload notes to revert optimistic update
      loadUserNotes();
    }
  };

  // Navigate to note in PDF (scroll only, no edit modal)
  const goToNoteInPDF = (note) => {
    // Close My Notes view first
    setShowMyNotes(false);

    // Reset the editing note to allow re-navigation to the same note
    setEditingNoteForPDF(null);

    // Use setTimeout to ensure state update happens after view closes
    setTimeout(() => {
      // Create a new object reference with timestamp and navigate-only flag
      const noteToNavigate = {
        ...note,
        _timestamp: Date.now(), // Add timestamp to force re-trigger
        _navigateOnly: true, // Flag to indicate navigation only (no edit modal)
      };
      // Set the note to navigate to (without opening edit modal)
      setEditingNoteForPDF(noteToNavigate);
      // The PDFSectionViewer will handle scrolling but NOT opening the edit modal
    }, 100);
  };

  // Edit note in PDF (scroll AND open edit modal)
  const editNoteInPDF = (note) => {
    // Close My Notes view first
    setShowMyNotes(false);

    // Reset the editing note to allow re-navigation to the same note
    setEditingNoteForPDF(null);

    // Use setTimeout to ensure state update happens after view closes
    setTimeout(() => {
      // Create a new object reference with timestamp to ensure React detects the change
      const noteToEdit = {
        ...note,
        _timestamp: Date.now(), // Add timestamp to force re-trigger
        _navigateOnly: false, // Flag to indicate edit mode (open modal)
      };
      // Set the note to navigate to and edit
      setEditingNoteForPDF(noteToEdit);
      // The PDFSectionViewer will handle scrolling and opening the edit modal
    }, 100);
  };

  // State for highlighting a specific highlight when navigating
  const [highlightedHighlightId, setHighlightedHighlightId] = useState(null);

  // State to track which content block the current selection belongs to (for Scoped Highlighting)
  const [selectedContentId, setSelectedContentId] = useState(null);

  // Scroll to highlight in PDF
  const scrollToHighlightInPDF = (highlight) => {
    // Close My Highlights view first
    setShowMyHighlights(false);

    // Reset the highlighted ID to allow re-navigation to the same highlight
    setHighlightedHighlightId(null);

    // Use setTimeout to ensure state update happens after view closes
    setTimeout(() => {
      // Set the highlight ID to navigate to
      setHighlightedHighlightId(highlight.id);
      // The PDFSectionViewer will handle scrolling to the highlight
    }, 100);
  };

  // Scroll to highlight in Short Notes
  const scrollToHighlightInShortNotes = (highlight) => {
    // Close My Highlights view first
    setShowMyHighlights(false);

    // Switch to Short Notes view
    setTimeout(() => {
      setShowNotes(true);

      // Wait for Short Notes to render, then scroll to highlight
      setTimeout(() => {
        if (notesRef.current) {
          // Find the highlight mark element
          const highlightMark = notesRef.current.querySelector(`mark[data-highlight-id="${highlight.id}"]`);
          if (highlightMark) {
            // Scroll to the highlight
            highlightMark.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });

            // Add animation class to highlight the element
            highlightMark.classList.add('highlight-navigating');
            setTimeout(() => {
              highlightMark.classList.remove('highlight-navigating');
            }, 2500);
          } else {
            // If highlight not found immediately, try again after a short delay
            setTimeout(() => {
              const retryMark = notesRef.current?.querySelector(`mark[data-highlight-id="${highlight.id}"]`);
              if (retryMark) {
                retryMark.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'nearest'
                });
                retryMark.classList.add('highlight-navigating');
                setTimeout(() => {
                  retryMark.classList.remove('highlight-navigating');
                }, 2500);
              }
            }, 500);
          }
        }
      }, 300);
    }, 100);
  };

  // Scroll to highlight in Class Notes
  const scrollToHighlightInClassNotes = (highlight) => {
    // Close My Highlights view first
    setShowMyHighlights(false);

    // Parse teacherNoteId from section_name format: "Class Notes|{teachernote_id}"
    const teacherNoteId = highlight.section_name?.split('|')[1];
    if (!teacherNoteId) return;

    // Helper function to navigate once notes are available
    const navigateIfNoteFound = (notes) => {
      const noteIndex = notes.findIndex(note => String(note.id) === teacherNoteId);
      if (noteIndex !== -1) {
        navigateToClassNoteHighlight(noteIndex, highlight.id);
        return true;
      }
      return false;
    };

    // Try to find note in current classNotes
    if (navigateIfNoteFound(classNotes)) {
      return;
    }

    // Note not found, load class notes first
    loadClassNotes().then(() => {
      // Use setTimeout to ensure state is updated
      setTimeout(() => {
        if (!navigateIfNoteFound(classNotes)) {
          console.warn(`Class Note ${teacherNoteId} not found after loading`);
        }
      }, 100);
    });
  };

  const navigateToClassNoteHighlight = (noteIndex, highlightId) => {
    // Switch to Class Notes view and set active note
    setTimeout(() => {
      setShowClassNotes(true);
      setShowNotes(false);
      setActiveClassNoteIndex(noteIndex);

      // Wait for Class Notes to render, then scroll to highlight
      setTimeout(() => {
        if (notesRef.current) {
          const highlightMark = notesRef.current.querySelector(`mark[data-highlight-id="${highlightId}"]`);
          if (highlightMark) {
            highlightMark.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });

            highlightMark.classList.add('highlight-navigating');
            setTimeout(() => {
              highlightMark.classList.remove('highlight-navigating');
            }, 2500);
          }
        }
      }, 300);
    }, 100);
  };

  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [showLearningPack, setShowLearningPack] = useState(false);
  const [floatingXP, setFloatingXP] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [showAskBudleeAIButton, setShowAskBudleeAIButton] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [showRemoveHighlight, setShowRemoveHighlight] = useState(false);
  const [removeHighlightPosition, setRemoveHighlightPosition] = useState({ top: 0, left: 0 });
  const [hoveredHighlight, setHoveredHighlight] = useState(null);
  const notesRef = useRef(null);

  // Highlights management - use chapterId if available, otherwise fallback to 'ch8'
  const highlightsKey = chapterId ? `chapter_${chapterId}` : 'ch8';
  const { highlights: highlightsArray, addHighlight, removeHighlight, isTextHighlighted, loading: highlightsLoading, reload: reloadHighlights } = useHighlights(highlightsKey);

  const mergedPdfHighlights = [
    ...(highlightsArray || []),
    ...(Array.isArray(citationHighlights) ? citationHighlights : []),
  ];

  const effectiveHighlightedHighlightId = citationHighlightedHighlightId ?? highlightedHighlightId;

  // If a citation requests navigation, ensure we're in PDF view.
  useEffect(() => {
    if (!citationHighlightedHighlightId) return;
    setShowNotes(false);
    setShowClassNotes(false);
    setShowMyNotes(false);
    setShowMyHighlights(false);
  }, [citationHighlightedHighlightId]);

  // Reload highlights when My Highlights view is opened
  useEffect(() => {
    if (showMyHighlights && chapterId && reloadHighlights) {
      reloadHighlights();
    }
  }, [showMyHighlights, chapterId, reloadHighlights]);

  // Removed useStickyNotes - now using backend notes exclusively via userNotes

  // Use chapterId as packId for backend annotations
  // This ensures all activities for the same chapter share completion tracking
  const packId = chapterId ? `chapter_${chapterId}` : 'default';
  const packState = useLearningPack(packId);

  // Reload packState when refreshKey changes (triggered by reset)
  useEffect(() => {
    if (refreshKey > 0) {
      packState.reloadFromStorage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]); // Only depend on refreshKey, not packState (which changes on every render)

  const handleActivityClick = (item) => {
    setSelectedActivity(item);
    setIsActivityModalOpen(true);
  };

  const handleActivityComplete = (itemId, xpReward, gemsReward = 0) => {
    // Check if already completed (for debugging)
    const wasAlreadyCompleted = packState.isItemCompleted(itemId);

    // Use XP reward (gems are handled by backend now, but we still track XP locally for Learning Pack)
    const earnedXP = packState.completeItem(itemId, xpReward);

    if (earnedXP > 0 || gemsReward > 0) {
      // Show floating XP/Gems text
      const rewardText = gemsReward > 0
        ? `+${earnedXP} XP, +${gemsReward} Gems`
        : `+${earnedXP} XP`;
      setFloatingXP({ amount: rewardText, timestamp: Date.now() });
      setTimeout(() => setFloatingXP(null), 1000);

      // Show toast notification
      if (gemsReward > 0) {
        toast.success(`🎉 ${rewardText} earned!`, {
          duration: 1000
        });
      } else {
        toast.success(`🎉 +${earnedXP} XP earned!`, {
          duration: 1000
        });
      }

      // Notify parent to update gems counter (if gems were earned)
      if (onXPEarned && (earnedXP > 0 || gemsReward > 0)) {
        onXPEarned(earnedXP + gemsReward); // Combine XP and gems for parent counter
      }

      console.log(`✅ Activity ${itemId} completed for the first time. Earned ${earnedXP} XP${gemsReward > 0 ? `, ${gemsReward} Gems` : ''}.`);
    } else if (wasAlreadyCompleted) {
      console.log(`ℹ️ Activity ${itemId} was already completed. No rewards awarded (preventing duplicate reward).`);
    }
  };

  const handleResetProgress = () => {
    if (window.confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
      packState.resetProgress();
      toast.success('Progress reset successfully!');
      // Reset XP in parent
      if (onXPEarned) {
        const currentXP = packState.getTotalXP();
        onXPEarned(-currentXP); // Subtract current XP to reset
      }
    }
  };

  // Removed handleAddNote - notes are now created via PDF click placement mode
  // Use handleAddNoteClick instead which creates notes in the PDF

  // Handle text selection in notes view (Short Notes and Class Notes)
  useEffect(() => {
    if ((!showNotes && !showClassNotes) || !notesRef.current) return;

    const handleTextSelection = (e) => {
      // Small delay to ensure selection is complete
      setTimeout(() => {
        const selection = window.getSelection();
        let text = '';

        // Custom selection logic to capture data-latex from images
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const fragment = range.cloneContents();

          const extractContent = (node) => {
            let content = '';
            if (node.nodeType === Node.TEXT_NODE) {
              content += node.textContent;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName === 'IMG' && node.hasAttribute('data-latex')) {
                content += node.getAttribute('data-latex');
              } else if (node.tagName === 'BR') {
                content += '\n';
              } else {
                node.childNodes.forEach(child => {
                  content += extractContent(child);
                });
              }
            } else if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
              node.childNodes.forEach(child => {
                content += extractContent(child);
              });
            }
            return content;
          };

          text = extractContent(fragment).trim();
        } else {
          text = selection?.toString().trim();
        }

        if (text && text.length > 0) {
          // Check if selection is within notes area
          const notesElement = notesRef.current;
          const range = selection.getRangeAt(0);

          if (notesElement && notesElement.contains(range.commonAncestorContainer)) {
            const rect = range.getBoundingClientRect();

            setSelectedText(text);

            // Scoped Highlighting: Find which content block this selection belongs to
            const contentContainer = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
              ? range.commonAncestorContainer.closest('[data-content-id]')
              : range.commonAncestorContainer.parentElement?.closest('[data-content-id]');

            const contentId = contentContainer ? contentContainer.getAttribute('data-content-id') : null;
            setSelectedContentId(contentId);

            setButtonPosition({
              top: rect.bottom + window.scrollY + 5,
              left: rect.left + window.scrollX
            });
            setShowAskBudleeAIButton(true);
          }
        } else {
          setShowAskBudleeAIButton(false);
        }
      }, 10);
    };

    const handleClickOutside = (e) => {
      // Close remove highlight button if clicking outside of highlighted text and button
      if (!e.target.closest('mark[data-highlight-id]') && !e.target.closest('button')) {
        setShowRemoveHighlight(false);
        setHoveredHighlight(null);
      }
    };

    const handleHighlightClick = (e) => {
      const mark = e.target.closest('mark[data-highlight-id]');
      if (mark) {
        const highlightId = mark.getAttribute('data-highlight-id');
        setHoveredHighlight(highlightId);
        const rect = mark.getBoundingClientRect();
        setRemoveHighlightPosition({
          top: rect.bottom + window.scrollY + 5,
          left: rect.left + window.scrollX
        });
        setShowRemoveHighlight(true);
        e.stopPropagation();

        // Scroll to highlight when clicked (for navigation within Short Notes)
        mark.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        // Add animation class to highlight the element
        mark.classList.add('highlight-navigating');
        setTimeout(() => {
          mark.classList.remove('highlight-navigating');
        }, 2500);
      }
    };

    // Listen for mouseup events (when user finishes selecting)
    document.addEventListener('mouseup', handleTextSelection);
    notesRef.current.addEventListener('click', handleHighlightClick);
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('mouseup', handleTextSelection);
      document.removeEventListener('click', handleClickOutside);
      if (notesRef.current) {
        notesRef.current.removeEventListener('click', handleHighlightClick);
      }
    };
  }, [showNotes, showClassNotes, highlightsArray]);

  const handleAskBudleeAIClick = () => {
    if (selectedText && onAskBudleeAI) {
      // If we're in Class Notes view, get the active note ID
      let teacherNoteId = null;
      if (showClassNotes && classNotes.length > 0 && activeClassNoteIndex < classNotes.length) {
        teacherNoteId = classNotes[activeClassNoteIndex].id;
      }
      // Pass selected text and optional teacherNoteId to parent
      onAskBudleeAI(selectedText, teacherNoteId);
      setShowAskBudleeAIButton(false);
      setSelectedText('');
      // Clear selection
      window.getSelection()?.removeAllRanges();
    }
  };

  const handleExplainClick = () => {
    if (selectedText && onAskBudleeAI) {
      let teacherNoteId = null;
      if (showClassNotes && classNotes.length > 0 && activeClassNoteIndex < classNotes.length) {
        teacherNoteId = classNotes[activeClassNoteIndex].id;
      }
      onAskBudleeAI(`Explain this: ${selectedText}`, teacherNoteId);
      setShowAskBudleeAIButton(false);
      setSelectedText('');
      window.getSelection()?.removeAllRanges();
    }
  };

  const handleReadAloudClick = () => {
    if (selectedText) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(selectedText);
      window.speechSynthesis.speak(utterance);

      setShowAskBudleeAIButton(false);
      setSelectedText('');
      window.getSelection()?.removeAllRanges();
    }
  };

  const handleHighlightClick = () => {
    if (selectedText && selectedText.trim().length > 0) {
      // Store the exact selected text without splitting
      // This allows partial selections, multi-line selections, and everything in between
      // Mark section_name based on current view
      let options = {};
      if (showNotes) {
        options = { section_name: 'Short Notes' };
      } else if (showClassNotes && classNotes.length > 0 && activeClassNoteIndex < classNotes.length) {
        // Store as "Class Notes|{teachernote_id}::{contentId}" to persist scope
        // Backend doesn't persist 'contentId' field, so we encode it in section_name
        const teacherNoteId = classNotes[activeClassNoteIndex].id;
        const scopeSuffix = selectedContentId ? `::${selectedContentId}` : '';
        options = {
          section_name: `Class Notes|${teacherNoteId}${scopeSuffix}`,
          contentId: selectedContentId // Keep passing it just in case, but rely on section_name
        };
      } else if (showBookmarks) {
        options = { section_name: 'Revision' };
      } else if (showMyNotes) {
        options = { section_name: 'My Notes' };
      }
      addHighlight(selectedText.trim(), options);
      toast.success('Text highlighted!');

      setShowAskBudleeAIButton(false);
      setSelectedText('');
      // Clear selection
      window.getSelection()?.removeAllRanges();
    }
  };



  const handleRemoveHighlightClick = () => {
    if (hoveredHighlight) {
      removeHighlight(hoveredHighlight);
      toast.success('Highlight removed!');
      setShowRemoveHighlight(false);
      setHoveredHighlight(null);
    }
  };




  // Helper to highlight within markdown without stripping syntax (for Short Notes)
  const highlightMarkdown = useCallback((text, scopeId = null) => {
    if (!text) return '';
    if (!highlightsArray || !highlightsArray.length) return text;

    let result = '';

    // 1. Tokenize text into Blocks (Text or Math)
    // Regex for $...$ (inline) and $$...$$ (block)
    const mathRegex = /(\$\$[\s\S]*?\$\$)|(\$(?:\\.|[^\$\\])*\$)/g;

    let lastIndex = 0;
    const blocks = [];
    let match;

    while ((match = mathRegex.exec(text)) !== null) {
      // Text before math
      if (match.index > lastIndex) {
        blocks.push({
          type: 'text',
          content: text.slice(lastIndex, match.index),
          start: lastIndex,
          end: match.index
        });
      }

      // Math block
      blocks.push({
        type: 'math',
        content: match[0],
        start: match.index,
        end: mathRegex.lastIndex
      });

      lastIndex = mathRegex.lastIndex;
    }

    // Remaining text
    if (lastIndex < text.length) {
      blocks.push({
        type: 'text',
        content: text.slice(lastIndex),
        start: lastIndex,
        end: text.length
      });
    }

    // Filter highlights based on scopeId (Content ID)
    // If scopeId is provided, ONLY show highlights that match this scopeId
    // If scopeId is NOT provided (e.g. legacy/Short Notes), show all highlights for now (or strictly global ones)
    const scopedHighlights = scopeId
      ? highlightsArray.filter(h => {
        // Check for direct property (optimistic update)
        if (h.contentId === scopeId) return true;
        // Check for encoded scope in section_name (persisted data)
        // Format: "Class Notes|{id}::{scopeId}"
        if (h.section_name && h.section_name.includes('::')) {
          const parts = h.section_name.split('::');
          const encodedScope = parts[parts.length - 1]; // Get last part
          return encodedScope === scopeId;
        }
        return false;
      })
      : highlightsArray;

    // 2. Identify ranges to highlight using Robust LCS Token Matching
    const ranges = new Array(text.length).fill(null);
    const sortedHighlights = [...scopedHighlights].sort((a, b) => b.text.length - a.text.length);

    // Create a mask for HTML tags to prevents highlighting them (which breaks nesting)
    const htmlTagMask = new Array(text.length).fill(false);
    const htmlTagRegex = /<\/?[a-z][\s\S]*?>/gi;
    let tagMatch;
    while ((tagMatch = htmlTagRegex.exec(text)) !== null) {
      for (let m = tagMatch.index; m < tagMatch.index + tagMatch[0].length; m++) {
        htmlTagMask[m] = true;
      }
    }

    // Reuse tokenizer logic - explicit unicode split for dashes/special chars
    const tokenize = (str) => str.split(/([a-zA-Z0-9]+)|([\-\u2013\u2014\u2212])|([^\w\s])/).filter(t => t && t.trim().length > 0);
    const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create a masked version of text where tags are replaced by spaces
    // This ensures contiguous word matching works even if a word is inside a tag (e.g. "<b>Header</b>")
    let maskedText = text;
    let maskMatch;
    const maskRegex = /<\/?[a-z][\s\S]*?>/gi;
    while ((maskMatch = maskRegex.exec(text)) !== null) {
      const spaces = ' '.repeat(maskMatch[0].length);
      maskedText = maskedText.substring(0, maskMatch.index) + spaces + maskedText.substring(maskMatch.index + maskMatch[0].length);
    }
    const textTokens = tokenize(maskedText); // Tokenize masked text (tags are now spaces)

    // Helper to canonicalize text for robust comparison
    // Normalizes dashes, whitespace, and casing
    const canonicalize = (str) => {
      return str
        .toLowerCase()
        .replace(/[\u2013\u2014\u2212]/g, '-') // Normalize dashes to hyphen
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();
    };

    sortedHighlights.forEach(highlight => {
      const highlightText = highlight.text.trim();
      if (!highlightText) return;

      const highlightTokens = tokenize(highlightText);
      // textTokens is already computed above

      if (!highlightTokens.length || !textTokens.length) return;

      // Algorithm: LCS to find best match of tokens
      let bestMatchStartIndex = -1;
      let bestMatchLength = 0;
      let bestMatchSpan = 0;

      for (let i = 0; i < textTokens.length; i++) {
        for (let j = 0; j < highlightTokens.length; j++) {
          let tOffset = 0;
          let hOffset = 0;
          let currentMatchLength = 0;
          let lastMeaningfulOffset = -1;

          // robust skippable punctuation regex with unicode escapes
          const replacePattern = /[#*_\-`>$!=+(){}\[\]^\\\/.,:;×·−\u2013\u2014\u2212"']+/g;
          const latexKeywords = new Set(['times', 'cdot', 'div', 'pm', 'approx', 'neq', 'leq', 'geq', 'in', 'ni', 'subset', 'supset', 'cap', 'cup', 'frac', 'sqrt', 'sum', 'prod', 'int', 'left', 'right', 'big', 'small', 'text', 'mathrm', 'mathbf', 'mathit', 'bm']);

          while ((i + tOffset) < textTokens.length && (j + hOffset) < highlightTokens.length) {
            const tTokenRaw = textTokens[i + tOffset];
            const hTokenRaw = highlightTokens[j + hOffset];
            // Use canonicalize for comparison
            const tTokenClean = canonicalize(tTokenRaw).replace(replacePattern, '');
            const hTokenClean = canonicalize(hTokenRaw).replace(replacePattern, '');

            if (!tTokenClean && tTokenRaw.match(replacePattern)) { tOffset++; continue; }
            if (!hTokenClean && hTokenRaw.match(replacePattern)) { hOffset++; continue; }

            if (latexKeywords.has(tTokenClean)) {
              const prevIndex = i + tOffset - 1;
              if (prevIndex >= 0 && textTokens[prevIndex] === '\\') { tOffset++; continue; }
            }

            if (tTokenClean === hTokenClean) {
              currentMatchLength++;
              lastMeaningfulOffset = tOffset + 1;
              tOffset++;
              hOffset++;
            } else {
              break;
            }
          }

          if (currentMatchLength > 0 && currentMatchLength > bestMatchLength) {
            bestMatchLength = currentMatchLength;
            bestMatchSpan = (lastMeaningfulOffset !== -1) ? lastMeaningfulOffset : tOffset;
            bestMatchStartIndex = i;
          }
        }
      }

      const isSignificant = (bestMatchLength === textTokens.length) || (bestMatchLength === highlightTokens.length) || (bestMatchLength >= 3);

      if (bestMatchLength > 0 && isSignificant) {
        const matchedTokens = textTokens.slice(bestMatchStartIndex, bestMatchStartIndex + bestMatchSpan);
        const escapedTokens = matchedTokens.map(escapeRegex);
        // Allow separator to skip over HTML tags OR non-alphanumeric chars
        const flexibleSeparator = '(?:<[^>]+>|[^a-zA-Z0-9]+)*';
        const pattern = escapedTokens.join(flexibleSeparator);

        try {
          const regex = new RegExp(pattern, 'gi'); // Global to find all occurrences if unique? No, textTokens is whole text.
          // We need to find the specific occurrence that corresponds to our tokens.
          // But since we are highlighting based on content, finding the *first* robust match or all matches is generally what we want.

          let match;
          while ((match = regex.exec(text)) !== null) {
            // Fill ranges
            let isOverlap = false;
            for (let k = match.index; k < match.index + match[0].length; k++) {
              if (ranges[k]) { isOverlap = true; break; }
            }
            if (!isOverlap) {
              for (let k = match.index; k < match.index + match[0].length; k++) {
                // Do not highlight masked HTML tags to preserve valid HTML structure
                if (!htmlTagMask[k]) {
                  ranges[k] = highlight.id;
                }
              }
            }
          }
        } catch (e) {
          console.warn('Highlight regex error in highlightMarkdown', e);
        }
      }
    });

    // 3. Rebuild text from blocks
    blocks.forEach(block => {
      if (block.type === 'text') {
        // Text Block: Use <mark> tags
        let buffer = '';
        let currentHighlightId = null;

        for (let i = 0; i < block.content.length; i++) {
          const globalIdx = block.start + i;
          const highlightId = ranges[globalIdx];

          if (highlightId !== currentHighlightId) {
            if (currentHighlightId) {
              buffer += '</mark>';
            }
            if (highlightId) {
              // Use consistent styling - reduced intensity for better readability
              buffer += `<mark data-highlight-id="${highlightId}" class="bg-yellow-200/60 text-black [&_*]:text-black px-0.5 rounded-sm cursor-pointer hover:bg-yellow-300/80 transition-colors" style="color: black !important; background-color: rgba(253, 224, 71, 0.5);">`;
            }
            currentHighlightId = highlightId;
          }
          buffer += block.content[i];
        }
        if (currentHighlightId) {
          buffer += '</mark>';
        }
        result += buffer;
      } else {
        // Math Block: Convert to Image
        // Check if significant part is highlighted
        let highlightedCount = 0;
        const globalStart = block.start;
        const globalEnd = block.end;

        // Get the dominant highlight ID if any
        let dominantHighlightId = null;
        const idCounts = {};

        for (let i = globalStart; i < globalEnd; i++) {
          const hid = ranges[i];
          if (hid) {
            highlightedCount++;
            idCounts[hid] = (idCounts[hid] || 0) + 1;
          }
        }

        // Determine if the block is considered "highlighted"
        // If > 0 chars are highlighted, we highlight the whole formula image for consistency
        const isHighlighted = highlightedCount > 0;

        if (isHighlighted) {
          // Find max count ID for data attribute
          dominantHighlightId = Object.keys(idCounts).reduce((a, b) => idCounts[a] > idCounts[b] ? a : b);
        }

        // Extract inner latex and clean it
        const isBlock = block.content.startsWith('$$');
        const delimiter = isBlock ? '$$' : '$';
        let inner = block.content.slice(delimiter.length, -delimiter.length);

        // Encode for URL
        const encodedFormula = encodeURIComponent(inner);
        const imgUrl = `https://latex.codecogs.com/svg.image?${encodedFormula}`;

        // Style: 
        // If highlighted: normal black text (on yellow bg) -> filter: none
        // If NOT highlighted: white text (on dark bg) -> filter: invert(1)
        // Note: We use 'inline-block' and 'align-middle' to align with text

        // Add data-latex attribute for selection handling
        const latexSource = block.content;

        const imgTag = `<img src="${imgUrl}" alt="${inner.replace(/"/g, '&quot;')}" data-latex="${latexSource.replace(/"/g, '&quot;')}" class="inline-block align-middle ${isHighlighted ? '' : 'invert'}" style="max-height: 2em; vertical-align: middle;" />`;

        if (isHighlighted) {
          // Wrap in mark with reduced intensity
          result += `<mark data-highlight-id="${dominantHighlightId}" class="bg-yellow-200/60 text-black px-0.5 rounded-sm cursor-pointer hover:bg-yellow-300/80 transition-colors inline-block" style="color: black !important; background-color: rgba(253, 224, 71, 0.5);">${imgTag}</mark>`;
        } else {
          result += imgTag;
        }
      }
    });

    return result;
  }, [highlightsArray]);


  // Reset notes when chapterId changes
  useEffect(() => {
    if (chapterId) {
      // Reset notes state when chapter changes
      setNotesContent(null);
      setNotesError(null);
      setLoadingNotes(false);
    }
  }, [chapterId]);

  // Fetch short notes when showNotes is toggled and chapterId is available
  useEffect(() => {
    if (showNotes && chapterId && !notesContent && !loadingNotes && !notesError) {
      setLoadingNotes(true);
      setNotesError(null);

      shortNotesAPI.generate(chapterId, true)
        .then((data) => {
          console.log('✅ Short notes generated:', data);
          setNotesContent(data);
          setLoadingNotes(false);
        })
        .catch((error) => {
          console.error('❌ Error generating short notes:', error);
          setNotesError(error.response?.data?.error || error.message || 'Failed to generate short notes');
          setLoadingNotes(false);

          // Fallback to default notes if available
          const fallbackNotes = {
            title: lesson?.title || "Chapter Summary",
            sections: [
              {
                heading: "Content",
                points: [
                  "Short notes are being generated. Please try again in a moment.",
                  "If the problem persists, contact support."
                ]
              }
            ]
          };
          setNotesContent(fallbackNotes);
        });
    }
  }, [showNotes, chapterId, notesContent, loadingNotes, notesError, lesson]);

  // Load class notes when showClassNotes is toggled and chapterId is available
  const loadClassNotes = useCallback(async () => {
    console.log(`🔄 loadClassNotes() called with chapterId=${chapterId}, loadingClassNotes=${loadingClassNotes}`);

    if (!chapterId) {
      console.warn('⚠️ Cannot load class notes: chapterId is missing');
      return;
    }

    console.log(`📚 Starting to load class notes for chapterId=${chapterId}`);
    setLoadingClassNotes(true);

    try {
      console.log(`📞 Calling getStudentTeacherNotes(${chapterId})...`);
      const notes = await getStudentTeacherNotes(chapterId);

      console.log(`✅ getStudentTeacherNotes() returned:`, {
        notes: notes,
        notesLength: notes?.length || 0,
        isArray: Array.isArray(notes),
        notesType: typeof notes
      });

      const notesArray = notes || [];
      console.log(`📝 Setting classNotes state to array with ${notesArray.length} note(s)`);

      setClassNotes(notesArray);
      setActiveClassNoteIndex(0); // Reset to first note

      console.log(`✅ Class notes loaded successfully. classNotes.length=${notesArray.length}`);

      if (notesArray.length > 0) {
        console.log(`🎉 Class Notes button SHOULD be visible (${notesArray.length} note(s) found)`);
      } else {
        console.warn(`⚠️ Class Notes button will NOT be visible (0 notes found)`);
      }
    } catch (error) {
      console.error('❌ Error loading class notes:', {
        error: error,
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      setClassNotes([]);
      console.warn(`⚠️ Setting classNotes to empty array due to error`);
    } finally {
      setLoadingClassNotes(false);
      console.log(`🏁 loadClassNotes() completed. loadingClassNotes set to false`);
    }
  }, [chapterId]);

  // Load class notes when chapterId changes (to check if button should be shown)
  useEffect(() => {
    console.log(`🔄 useEffect [chapterId, loadClassNotes] triggered:`, {
      chapterId: chapterId,
      loadingClassNotes: loadingClassNotes,
      shouldLoad: chapterId && !loadingClassNotes
    });

    if (chapterId && !loadingClassNotes) {
      console.log(`📚 Chapter changed, loading class notes...`);
      loadClassNotes();
    } else {
      console.log(`⏸️ Skipping loadClassNotes:`, {
        hasChapterId: !!chapterId,
        isLoading: loadingClassNotes
      });
    }
  }, [chapterId, loadClassNotes]);

  // Also reload when showClassNotes is toggled (in case notes were added/removed)
  useEffect(() => {
    console.log(`🔄 useEffect [showClassNotes, chapterId, loadClassNotes] triggered:`, {
      showClassNotes: showClassNotes,
      chapterId: chapterId,
      loadingClassNotes: loadingClassNotes,
      shouldLoad: showClassNotes && chapterId && !loadingClassNotes
    });

    if (showClassNotes && chapterId && !loadingClassNotes) {
      console.log(`📚 Class Notes view opened, reloading notes...`);
      loadClassNotes();
    }
  }, [showClassNotes, chapterId, loadClassNotes]);

  // Log when classNotes state changes
  useEffect(() => {
    console.log(`📊 classNotes state changed:`, {
      count: classNotes.length,
      notes: classNotes,
      buttonShouldShow: classNotes.length > 0
    });
  }, [classNotes]);

  // Extract learning pack items from lesson if available
  useEffect(() => {
    if (lesson?.learningPackData?.sections) {
      setLearningPackItems(lesson.learningPackData.sections);
    }
  }, [lesson]);

  const stripLeadingSectionNumber = (value) => {
    if (!value || typeof value !== 'string') return value;
    return value
      .replace(/^\s*(?:section\s*)?\d+(?:\.\d+)*\s*[:\-–—.)]\s*/i, '')
      .replace(/^\s*(?:section\s*)?\d+(?:\.\d+)*\s+(?=[A-Za-z(])/i, '')
      .replace(/^\s*\d+(?:\.\d+)*\s*[:\-–—.)]\s*/i, '')
      .replace(/^\s*\d+(?:\.\d+)*\s+(?=[A-Za-z(])/i, '')
      .trim();
  };

  const currentSectionName = stripLeadingSectionNumber(
    learningPackItems[currentLearningPackIndex]?.name ||
    learningPackItems[currentLearningPackIndex]?.title ||
    learningPackItems[currentLearningPackIndex]?.fullTitle ||
    'Section'
  );

  const handleTextbookClick = () => {
    setShowNotes(false);
    setShowClassNotes(false);
    setShowMyNotes(false);
    setShowMyHighlights(false);
    setShowLearningPack(false);
    setShowBookmarks(false);
  };

  const handleTeacherNotesClick = () => {
    setShowClassNotes(!showClassNotes);
    setShowNotes(false);
    setShowMyNotes(false);
    setShowMyHighlights(false);
    setShowLearningPack(false);
    setShowBookmarks(false);
  };

  const handleShortNotesClick = () => {
    setShowNotes(!showNotes);
    setShowClassNotes(false);
    setShowMyNotes(false);
    setShowMyHighlights(false);
    setShowLearningPack(false);
    setShowBookmarks(false);
  };


  const handlePreviousSection = () => {
    if (currentLearningPackIndex > 0) {
      setCurrentLearningPackIndex(currentLearningPackIndex - 1);
    }
  };

  const handleNextSection = () => {
    if (currentLearningPackIndex < learningPackItems.length - 1) {
      setCurrentLearningPackIndex(currentLearningPackIndex + 1);
    }
  };

  const activeHeaderView = showNotes
    ? 'short'
    : showClassNotes
      ? 'class'
      : showMyNotes
        ? 'my_notes'
        : showMyHighlights
          ? 'highlights'
          : '';

  // When none of the notes/highlights views are active, we're in the main Textbook view
  const isTextbookActive = !showNotes && !showClassNotes && !showMyNotes && !showMyHighlights;

  const setHeaderView = (nextView) => {
    if (!nextView) {
      setShowNotes(false);
      setShowClassNotes(false);
      setShowMyNotes(false);
      setShowMyHighlights(false);
      setShowBookmarks(false);
      return;
    }

    if (nextView === 'my_notes') {
      handleMyNotesClick();
      return;
    }

    if (nextView === 'highlights') {
      handleMyHighlightsClick();
      return;
    }

    if (nextView === 'class') {
      setShowClassNotes(true);
      setShowNotes(false);
      setShowMyNotes(false);
      setShowMyHighlights(false);
      setShowBookmarks(false);
      return;
    }

    // short notes
    setShowNotes(true);
    setShowClassNotes(false);
    setShowMyNotes(false);
    setShowMyHighlights(false);
    setShowBookmarks(false);
  };

  const shortNotesView = useMemo(() => (
    <div className="relative">
      <div ref={notesRef} className="notes-view-viewer p-8 pt-0 space-y-6">
        {loadingNotes ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))] mb-4" />
            <p className="text-muted-foreground">Generating comprehensive short notes...</p>
            <p className="text-sm text-muted-foreground mt-2">This may take a moment</p>
          </div>
        ) : notesError ? (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-red-500 mb-2">Error: {notesError}</p>
            <Button
              onClick={() => {
                setNotesContent(null);
                setNotesError(null);
                setLoadingNotes(false);
              }}
              variant="outline"
              size="sm"
            >
              Retry
            </Button>
          </div>
        ) : notesContent ? (
          <>
            <div className="flex items-start justify-between gap-4 mb-8 border-b border-[hsl(var(--card-border))] pb-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                <ReactMarkdown
                  remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                  rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                >
                  {highlightMarkdown(sanitizeChatMarkdown(notesContent.title))}
                </ReactMarkdown>
              </h1>
            </div>

            {notesContent.sections && notesContent.sections.length > 0 ? (
              notesContent.sections.map((section, index) => (
                <div key={index} className="space-y-3">
                  {section.heading && (
                    <div className="flex items-center justify-between gap-2 mb-3 mt-6 first:mt-0">
                      <h2 className="text-xl font-bold text-[hsl(var(--teal-vivid))]">
                        <ReactMarkdown
                          remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                          rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                        >
                          {highlightMarkdown(sanitizeChatMarkdown(section.heading))}
                        </ReactMarkdown>
                      </h2>
                      <BookmarkIcon
                        chapterId={chapterId}
                        sectionName={section.heading} // Group by Heading
                        bookmarkType="note"
                        contentId={`section_${btoa(unescape(encodeURIComponent(section.heading || ''))).substring(0, 20)}`}
                        contentData={{
                          title: section.heading,
                          preview_text: section.points?.[0]?.substring(0, 100) || "Section content"
                        }}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-6">
                    {/* One card per point - Enhanced with structured parsing */}
                    {(() => {
                      const points = section.points || [];
                      // Use section heading (if meaningful) or main title as context // Enhanced contextual title logic
                      const contextTitle = section.heading && !section.heading.match(/key points|summary|notes/i)
                        ? section.heading
                        : ((notesContent.title || '').replace(/^Chapter \d+[:\s]*/i, '') || "Introduction");
                      const processedPoints = processNotePoints(points, contextTitle); // Merge orphan examples

                      return processedPoints.map((parsed, idx) => {
                        if (!parsed) return null;

                        return (
                          <Card
                            key={idx}
                            className="border-[hsl(var(--card-border))] bg-neutral-900/50 shadow-md hover:shadow-lg transition-all duration-300 relative group"
                          >
                            {/* Bookmark Icon - Top Right of Card */}
                            <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                              <BookmarkIcon
                                chapterId={chapterId}
                                sectionName={section.heading}
                                sectionNumber={null} // Auto-detect section number
                                bookmarkType="note"
                                contentId={`note_${btoa(unescape(encodeURIComponent(parsed.raw || ''))).substring(0, 20)}`}
                                contentData={{
                                  title: `${section.heading} - ${parsed.term || `Card ${idx + 1}`}`,
                                  preview_text: parsed.definition || parsed.raw,
                                  full_text: parsed.raw,
                                  point_index: idx
                                }}
                                className="bg-background/80 backdrop-blur-sm shadow-sm hover:bg-background"
                              />
                            </div>

                            <CardContent className="p-6 pr-10">
                              {parsed.hasStructure ? (
                                /* Structured rendering with visual hierarchy */
                                <div className="space-y-3">
                                  {/* Term Header */}
                                  <h3 className="text-lg font-bold text-[hsl(var(--teal-vivid))] mb-2">
                                    {parsed.term}
                                  </h3>

                                  {/* Definition */}
                                  <div className="text-muted-foreground leading-relaxed [&_strong]:text-foreground [&_strong]:font-semibold [&_.katex]:text-foreground text-[15px]">
                                    <ReactMarkdown
                                      remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                      rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                                    >
                                      {highlightMarkdown(sanitizeChatMarkdown(parsed.definition))}
                                    </ReactMarkdown>
                                  </div>

                                  {/* Examples - Highlighted boxes */}
                                  {parsed.examples && parsed.examples.length > 0 && (
                                    <div className="space-y-2 mt-4">
                                      {parsed.examples.map((example, exIdx) => (
                                        <div
                                          key={exIdx}
                                          className="bg-blue-500/10 border-l-4 border-blue-500/50 rounded-r-md p-3"
                                        >
                                          <div className="flex items-start gap-2">
                                            <span className="text-xs font-semibold text-blue-400 uppercase tracking-wide shrink-0">
                                              Example:
                                            </span>
                                            <div className="text-sm text-muted-foreground leading-relaxed [&_.katex]:text-foreground">
                                              <ReactMarkdown
                                                remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                                rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                                              >
                                                {sanitizeChatMarkdown(example)}
                                              </ReactMarkdown>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Formulas - Special formatting */}
                                  {parsed.formulas && parsed.formulas.length > 0 && (
                                    <div className="space-y-2 mt-4">
                                      {parsed.formulas.map((formula, formIdx) => (
                                        <div
                                          key={formIdx}
                                          className="bg-purple-500/10 border-l-4 border-purple-500/50 rounded-r-md p-3"
                                        >
                                          <div className="flex items-start gap-2">
                                            <span className="text-xs font-semibold text-purple-400 uppercase tracking-wide shrink-0">
                                              Formula:
                                            </span>
                                            <div className="text-sm text-foreground leading-relaxed [&_.katex]:text-foreground">
                                              <ReactMarkdown
                                                remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                                rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                                              >
                                                {formula}
                                              </ReactMarkdown>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                /* Fallback: Raw rendering for old format */
                                <div className="text-muted-foreground leading-relaxed [&_strong]:text-foreground [&_strong]:font-semibold [&_.katex]:text-foreground [&_mark_*]:!text-black text-[15px]">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                    rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                                  >
                                    {highlightMarkdown(sanitizeChatMarkdown(parsed.raw))}
                                  </ReactMarkdown>

                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      });
                    })()}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground">No sections available.</p>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <BookText className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No short notes available.</p>
          </div>
        )}
      </div>
    </div>
  ), [loadingNotes, notesError, notesContent, chapterId, highlightMarkdown]);

  const classNotesView = useMemo(() => (
    <div className="w-full">
      <div ref={notesRef} className="notes-view-viewer p-3 sm:p-8 pt-0 space-y-6">
        {loadingClassNotes ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))] mb-4" />
            <p className="text-muted-foreground">Loading class notes...</p>
          </div>
        ) : classNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <BookOpen className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No class notes available for this chapter.</p>
          </div>
        ) : (
          <>
            {/* Navigation for multiple notes */}
            {classNotes.length > 1 && (
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-[hsl(var(--card-border))]">
                <Button
                  onClick={() => setActiveClassNoteIndex(Math.max(0, activeClassNoteIndex - 1))}
                  disabled={activeClassNoteIndex === 0}
                  variant="outline"
                  size="sm"
                  className="text-[hsl(var(--teal-vivid))] border-[hsl(var(--teal-vivid))] hover:bg-[hsl(var(--teal-vivid))] hover:text-white disabled:text-muted-foreground disabled:border-muted disabled:opacity-50"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Note {activeClassNoteIndex + 1} of {classNotes.length}
                </span>
                <Button
                  onClick={() => setActiveClassNoteIndex(Math.min(classNotes.length - 1, activeClassNoteIndex + 1))}
                  disabled={activeClassNoteIndex === classNotes.length - 1}
                  variant="outline"
                  size="sm"
                  className="text-[hsl(var(--teal-vivid))] border-[hsl(var(--teal-vivid))] hover:bg-[hsl(var(--teal-vivid))] hover:text-white disabled:text-muted-foreground disabled:border-muted disabled:opacity-50"
                >
                  <ArrowLeft className="w-4 h-4 rotate-180" />
                </Button>
              </div>
            )}

            {/* Render active note */}
            {/* Render active note */}
            {classNotes[activeClassNoteIndex] && (
              <>
                <h1
                  className="text-2xl sm:text-3xl font-bold text-white mb-8 border-b border-[hsl(var(--card-border))] pb-4"
                  data-content-id={`teacher_note_${classNotes[activeClassNoteIndex].id}_title`}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                    rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                  >
                    {highlightMarkdown(classNotes[activeClassNoteIndex].title || 'Class Notes', `teacher_note_${classNotes[activeClassNoteIndex].id}_title`)}
                  </ReactMarkdown>
                </h1>

                {/* Check for structured content first */}
                {classNotes[activeClassNoteIndex].structured_content &&
                  classNotes[activeClassNoteIndex].structured_content.sections ? (
                  <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-8 max-w-4xl">
                    {classNotes[activeClassNoteIndex].structured_content.sections.map((section, sectionIdx) => (
                      <div key={sectionIdx} className="space-y-3">
                        {section.heading && (
                          <div className="flex items-center justify-between gap-2 mb-3 mt-6 first:mt-0">
                            <h2
                              className="text-xl font-bold text-[hsl(var(--teal-vivid))]"
                              data-content-id={`teacher_note_${classNotes[activeClassNoteIndex].id}_${sectionIdx}_heading`}
                            >
                              <ReactMarkdown
                                remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                              >
                                {highlightMarkdown(section.heading, `teacher_note_${classNotes[activeClassNoteIndex].id}_${sectionIdx}_heading`)}
                              </ReactMarkdown>
                            </h2>
                            <BookmarkIcon
                              chapterId={chapterId}
                              sectionName={section.heading}
                              bookmarkType="note"
                              contentId={`teacher_note_${classNotes[activeClassNoteIndex].id}_${sectionIdx}`}
                              contentData={{
                                title: section.heading,
                                preview_text: section.points?.[0]?.substring(0, 100) || "Section content",
                                note_id: classNotes[activeClassNoteIndex].id
                              }}
                            />
                          </div>
                        )}

                        {/* Points as Single Narrative Card - Mimic Short Notes Style */}
                        <div className="grid grid-cols-1 gap-6">
                          {(() => {
                            const combinedPoints = section.points ? section.points.join('\n\n') : '';
                            if (!combinedPoints) return null;

                            const combinedContentId = `teacher_note_${classNotes[activeClassNoteIndex].id}_${sectionIdx}_combined`;

                            return (
                              <Card
                                className="border-[hsl(var(--card-border))] bg-neutral-900/50 shadow-md hover:shadow-lg transition-all duration-300 relative group"
                              >
                                {/* Bookmark Icon - Top Right of Card */}
                                <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <BookmarkIcon
                                    chapterId={chapterId}
                                    sectionName={section.heading || classNotes[activeClassNoteIndex].title}
                                    sectionNumber={null} // Auto-detect section number
                                    bookmarkType="note"
                                    contentId={combinedContentId}
                                    contentData={{
                                      title: `${classNotes[activeClassNoteIndex].title} - ${section.heading || 'Section'}`,
                                      preview_text: combinedPoints,
                                      full_text: combinedPoints,
                                      note_id: classNotes[activeClassNoteIndex].id
                                    }}
                                    className="bg-background/80 backdrop-blur-sm shadow-sm hover:bg-background"
                                  />
                                </div>

                                <CardContent className="p-6 pr-10">
                                  <div
                                    className="text-muted-foreground leading-relaxed [&_strong]:text-foreground [&_strong]:font-semibold [&_.katex]:text-foreground [&_mark_*]:!text-black text-[15px] whitespace-pre-wrap"
                                    data-content-id={combinedContentId}
                                  >
                                    <ReactMarkdown
                                      remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                      rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                                    >
                                      {highlightMarkdown(combinedPoints, combinedContentId)}
                                    </ReactMarkdown>
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Fallback to raw HTML content */
                  <div className="prose prose-invert max-w-none text-foreground [&_a]:text-[hsl(var(--green-bright))] [&_a]:hover:text-[hsl(var(--teal-vivid))] [&_a]:underline [&_a]:underline-offset-2">
                    <ReactMarkdown
                      remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                      rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                    >
                      {highlightMarkdown(classNotes[activeClassNoteIndex].content || '')}
                    </ReactMarkdown>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  ), [loadingClassNotes, classNotes, activeClassNoteIndex, chapterId, highlightMarkdown]);


  return (
    <div className={`relative w-full h-full px-1 py-1 sm:px-3 sm:py-2 flex flex-col ${fillParentHeight ? 'min-h-0' : ''}`}>
      {/* Navigation Row: Textbook, Teacher's Notes, Short Notes */}
      <div className="mb-2 flex items-center justify-between gap-1 flex-shrink-0 relative z-10">
        <div className="flex items-center gap-1">
          <Button
            onClick={onClose}
            variant="outline"
            size="icon"
            aria-label="Back to Learning Path"
            className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>

          {/* Textbook / Teacher's Notes / Short Notes Tabs (grouped left next to back button) */}
          <div className="flex items-center gap-0.5 sm:gap-1 rounded-lg border border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))] p-0.5 sm:p-1">
            <Button
              onClick={handleTextbookClick}
              variant={!showNotes && !showClassNotes ? 'default' : 'ghost'}
              size="sm"
              data-testid="textbook-tab"
              className={`${!showNotes && !showClassNotes
                ? 'bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--teal-glow))]'
                : 'text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10'
                }`}
            >
              <BookText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className={`${(!showNotes && !showClassNotes && !showBookmarks && !showMyNotes && !showMyHighlights) ? 'inline' : 'hidden'} sm:inline`}>
                Textbook
              </span>
            </Button>

            {classNotes.length > 0 && (
              <Button
                onClick={handleTeacherNotesClick}
                variant={showClassNotes ? 'default' : 'ghost'}
                size="sm"
                className={`text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 ${showClassNotes
                  ? 'bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--teal-glow))]'
                  : 'text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10'
                  }`}
              >
                <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className={`${showClassNotes ? 'inline' : 'hidden'} sm:inline`}>
                  Teacher's Notes
                </span>
              </Button>
            )}

            <Button
              onClick={handleShortNotesClick}
              variant={showNotes ? 'default' : 'ghost'}
              size="sm"
              data-testid="short-notes-tab"
              className={`text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 ${showNotes
                ? 'bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--teal-glow))]'
                : 'text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10'
                }`}
            >
              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className={`${showNotes ? 'inline' : 'hidden'} sm:inline`}>
                Short Notes
              </span>
            </Button>

            <Button
              onClick={handleBookmarksClick}
              variant={showBookmarks ? 'default' : 'ghost'}
              size="sm"
              data-testid="revision-tab"
              className={`text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 ${showBookmarks
                ? 'bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--teal-glow))]'
                : 'text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10'
                }`}
            >
              <NotebookText className="w-4 h-4" />
              <span className="hidden sm:inline">Revision</span>
            </Button>
          </div>
        </div>

        {/* Notes / Highlights buttons - show in same place as PDF viewer header */}
        <div className="flex items-center gap-2">
          {(showNotes || showClassNotes) ? (
            <Button
              onClick={handleMyNotesClick}
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 bg-yellow-600/90 border-yellow-600 text-white hover:bg-yellow-700 font-semibold"
            >
              <StickyNoteIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Notes</span>
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="notes-button"
                  className="text-xs sm:text-sm px-2 sm:px-3 h-8 sm:h-9 bg-yellow-600/90 border-yellow-600 text-white hover:bg-yellow-700 font-semibold"
                >
                  <StickyNoteIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Notes</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[hsl(var(--card-bg))] text-white border-[hsl(var(--card-border))]">
                {lesson?.pdfUrl && !showNotes && !showClassNotes && (
                  <DropdownMenuItem
                    onClick={handleAddNoteClick}
                    className="text-gray-100 cursor-pointer hover:bg-[hsl(var(--primary))]/20 hover:text-white"
                  >
                    <StickyNoteIcon className="w-4 h-4 mr-2" />
                    Add Note
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={handleMyNotesClick}
                  data-testid="view-notes-item"
                  className="text-gray-100 cursor-pointer hover:bg-[hsl(var(--primary))]/20 hover:text-white"
                >
                  <BookText className="w-4 h-4 mr-2" />
                  View Notes
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button
            onClick={handleMyHighlightsClick}
            variant="outline"
            size="sm"
            data-testid="highlights-tab"
            className="text-gray-300 border-gray-500 hover:bg-[hsl(var(--primary))]/20 hover:text-white"
          >
            <Highlighter className="w-4 h-4" />
            <span className="hidden sm:inline">Highlights</span>
          </Button>
        </div>
      </div>

      {/* Learning Pack Navigation Row */}
      {learningPackItems.length > 0 && (
        <div className="mb-2 flex items-center gap-1 flex-shrink-0 relative z-10">
          <Button
            onClick={handlePreviousSection}
            disabled={currentLearningPackIndex === 0}
            variant="outline"
            size="icon"
            className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 text-[hsl(var(--primary))]" />
          </Button>

          <div className="flex-1 rounded-lg border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-3 py-1.5 text-center">
            <p className="text-sm font-medium text-[hsl(var(--primary))]">{currentSectionName}</p>
          </div>

          <Button
            onClick={handleNextSection}
            disabled={currentLearningPackIndex === learningPackItems.length - 1}
            variant="outline"
            size="icon"
            className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4 text-[hsl(var(--primary))]" />
          </Button>
        </div>
      )}

      {/* Content Area - Scrollable */}
      <div className={`w-full bg-[hsl(var(--card-bg))] rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col ${fillParentHeight ? 'flex-1 min-h-0 flex flex-col' : ''}`}>
        <div
          className={`relative w-full h-full ${fillParentHeight
            ? `flex-1 min-h-0 ${(isPdfViewActive && lesson?.pdfUrl)
              ? 'overflow-hidden'
              : 'overflow-y-auto'
            }`
            : `max-h-[calc(100vh-200px)] ${(isPdfViewActive && lesson?.pdfUrl)
              ? 'h-[calc(100vh-200px)] overflow-hidden'
              : 'overflow-y-auto'
            }`
            }`}
        >
          <div className={isPdfViewActive ? 'h-full' : 'hidden'} data-testid="pdf-view">
            {/* PDF Viewer - from abdul Learn feature */}
            <div className="relative h-full">
              <div className="relative h-full">
                {/* PDF Section Viewer - Show if PDF URL is available */}
                {lesson?.pdfUrl ? (
                  <div className="w-full h-full">
                    {/* Mobile split: allow parent to hide PDF to reveal chat/bottom pane */}
                    {(!isMobileSplit || showPdf) ? (
                      <PDFSectionViewer
                        pdfUrl={lesson.pdfUrl}
                        authToken={getAuthToken(getPortalType(window.location.pathname || ''))}
                        onClose={onClose}
                        chapterId={chapterId}
                        initialPage={pdfInitialPage}
                        contentPaddingClass={pdfFullBleed ? 'p-0' : 'p-4'}
                        pageAlignment={pdfFullBleed ? 'start' : 'center'}
                        pageSpacingClass={pdfFullBleed ? 'mb-0' : 'mb-4'}
                        containerBgClass={pdfFullBleed ? 'bg-white' : 'bg-gray-100'}
                        prefetchedSections={chapterSectionsData.sections}
                        prefetchedNumPages={chapterSectionsData.numPages}
                        onAddNote={isTextbookActive ? null : handleAddNoteClick}
                        onViewNotes={isTextbookActive ? null : handleMyNotesClick}
                        onViewHighlights={isTextbookActive ? null : handleMyHighlightsClick}
                        notes={userNotes.filter(note => note.position !== null)}
                        // Allow parent to control sizing for mobile split layouts
                        containerClass={isMobileSplit ? 'h-full w-full overflow-auto' : ''}
                        onNoteUpdate={handleUpdateNote}
                        onNoteDelete={handleDeleteNote}
                        onNotePageChange={handleNotePageChange}
                        onNotesReload={loadUserNotes}
                        autoEditNote={editingNoteForPDF}
                        onNoteEditComplete={() => {
                          setEditingNoteForPDF(null);
                        }}
                        chapterAnnotations={effectiveChapterAnnotations}
                        onActivityClick={handleActivityClick}
                        onShowLearningPack={(isOpen, ctx = null) => {
                          setShowLearningPack(isOpen);
                          setLearningPackSectionContext(ctx);
                          const nextSectionNumber =
                            (typeof ctx === 'string' || typeof ctx === 'number')
                              ? String(ctx)
                              : (ctx && (ctx.sectionNumber ?? ctx.section_number))
                                ? String(ctx.sectionNumber ?? ctx.section_number)
                                : null;
                          setLearningPackSectionNumber(nextSectionNumber);
                        }}
                        learningPackActivities={effectiveChapterAnnotations.filter(isLearningPackAnnotation).map(ann => {
                          const inferredSectionNumber = inferSectionNumberFromPage(ann.page_number);
                          const effectiveSectionNumber = ann.section_number ?? ann.sectionNumber ?? inferredSectionNumber ?? null;
                          return {
                            id: ann.id,
                            annotation_id: ann.id,
                            type: ann.annotation_type,
                            section_number: effectiveSectionNumber,
                            section_name: ann.section_name ?? ann.sectionName ?? null,
                            page_number: ann.page_number,
                            title: ann.title || ann.annotation_type,
                            description: ann.description || '',
                            one_liner: ann.one_liner ?? ann.oneLiner ?? null,
                            xpReward: ann.xp_reward || 0,
                            duration: (ann.duration_seconds ? `${Math.max(Math.ceil(ann.duration_seconds / 60), 1)} min` : ann.duration) || '',
                            durationSeconds: ann.duration_seconds || 0
                          };
                        })}
                        onAskBudleeAI={onAskBudleeAI}
                        highlights={mergedPdfHighlights}
                        addHighlight={addHighlight}
                        removeHighlight={removeHighlight}
                        highlightedHighlightId={effectiveHighlightedHighlightId}
                        userAnnotationPositions={userAnnotationPositions}
                        onAnnotationPositionUpdate={loadAnnotationPositions}
                        isActivityCompleted={(activityId) => {
                          // Check backend completions first (source of truth), then fallback to localStorage
                          const activityKey = String(activityId);
                          return !!backendCompletions[activityKey] || packState.isItemCompleted(activityId);
                        }}
                        targetSection={targetSection}
                        targetAnnotationId={targetAnnotationId}
                        activeAnnotationId={activeAnnotationId}
                        onClearTargetAnnotation={onClearTargetAnnotation}
                        onClearActiveAnnotation={onClearActiveAnnotation}
                        onSectionScroll={onSectionScroll}
                        onBookmarkChange={() => setBookmarksRefreshTrigger(prev => prev + 1)}
                        activeBookmarks={activeBookmarks}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Button onClick={() => { if (onTogglePdf) onTogglePdf(); }} className="bg-[hsl(var(--primary))] text-white">Show PDF</Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-[hsl(var(--main-bg))] rounded-lg border border-[hsl(var(--card-border))] p-8">
                    <BookText className="w-16 h-16 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">No PDF Available</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Please select a subject and chapter from the dropdowns above to view the PDF content.
                    </p>
                    {onClose && (
                      <Button onClick={onClose} variant="outline" className="mt-4">
                        Go Back
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={isPdfViewActive ? 'hidden' : ''}>
            {showBookmarks ? (
              <div className="p-3 sm:p-6 w-full">
                <BookmarksView
                  chapterId={chapterId}
                  chapterName={chapterSectionsData?.title || `Chapter ${chapterId}`}
                  sections={chapterSectionsData.sections}
                  onNavigate={handleBookmarkNavigation}
                  chapterAnnotations={effectiveChapterAnnotations}
                  packState={packState}
                  backendCompletions={backendCompletions}
                  onActivityComplete={handleActivityComplete}
                  selectedTab={bookmarksTab}
                  onTabChange={setBookmarksTab}
                  refreshTrigger={bookmarksRefreshTrigger}
                  onBookmarksUpdated={fetchActiveBookmarks}
                />
              </div>
            ) : showMyNotes ? (
              /* My Notes View */
              <div className="p-3 sm:p-6 w-full">
                <h2 className="text-2xl font-bold text-foreground mb-6">My Notes</h2>

                {loadingUserNotes ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))] mr-2" />
                    <p className="text-muted-foreground">Loading notes...</p>
                  </div>
                ) : userNotes.length === 0 ? (
                  <div className="text-center py-12">
                    <StickyNoteIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground text-lg mb-2">No notes yet</p>
                    <p className="text-muted-foreground text-sm mb-4">Click "Add Note" to create your first note</p>
                    <Button onClick={handleAddNoteClick} className="bg-yellow-600 hover:bg-yellow-700 text-white">
                      <StickyNoteIcon className="w-4 h-4 mr-2" />
                      Add Note
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userNotes.map((note) => (
                      <div
                        key={note.id}
                        className="bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg p-4 hover:border-[hsl(var(--primary))] transition-colors group"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            {note.heading && (
                              <h3 className="text-lg font-semibold text-foreground mb-1">{note.heading}</h3>
                            )}
                            <p className="text-sm text-muted-foreground mb-2">
                              Chapter {note.chapter?.order || 'N/A'}: {note.chapter?.chapter_name || 'Unknown'} - Page {note.page_number}
                            </p>
                            {note.content && (
                              <p className="text-foreground whitespace-pre-wrap">{note.content}</p>
                            )}
                            {!note.content && (
                              <p className="text-muted-foreground italic">Empty note - click to edit</p>
                            )}
                            {note.selected_text && (
                              <div className="mt-2 p-2 bg-[hsl(var(--card-bg))] rounded text-sm text-muted-foreground italic border-l-2 border-[hsl(var(--primary))]">
                                "{note.selected_text}"
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click
                                // Navigate to note location in PDF (no edit modal)
                                goToNoteInPDF(note);
                              }}
                              title="Go to note location"
                              className="text-foreground border-[hsl(var(--card-border))] hover:bg-[hsl(var(--primary))]/20 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
                            >
                              <BookText className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click
                                // Navigate to note and open edit modal
                                editNoteInPDF(note);
                              }}
                              title="Edit note"
                              className="text-foreground border-[hsl(var(--card-border))] hover:bg-[hsl(var(--primary))]/20 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click
                                handleDeleteNote(note.id);
                              }}
                              className="text-red-400 border-red-500/50 hover:bg-red-500/20 hover:border-red-500 hover:text-red-300"
                              title="Delete note"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                          <span className={`w-3 h-3 rounded-full bg-${note.color}-500`}></span>
                          <span>{new Date(note.created_at).toLocaleDateString()}</span>
                          {note.is_pinned && <span>📌 Pinned</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : showMyHighlights ? (
              /* My Highlights View */
              <div className="p-3 sm:p-6 w-full">
                <h2 className="text-2xl font-bold text-white mb-6">My Highlights</h2>

                {highlightsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))] mr-2" />
                    <p className="text-muted-foreground">Loading highlights...</p>
                  </div>
                ) : !highlightsArray || highlightsArray.length === 0 ? (
                  <div className="text-center py-12">
                    <Highlighter className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground text-lg mb-2">No highlights yet</p>
                    <p className="text-muted-foreground text-sm mb-4">Select text in the PDF and click "Highlight" to create your first highlight</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {highlightsArray.map((highlight) => (
                      <div
                        key={highlight.id}
                        className="bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg p-4 hover:border-[hsl(var(--primary))] transition-colors cursor-pointer group"
                        onClick={(e) => {
                          // Only navigate if clicking on the card itself, not on buttons
                          if (e.target.closest('button')) {
                            return; // Let button handle its own click
                          }
                          // Navigate to highlight location - check if it's from Short Notes, Class Notes, or PDF
                          const isShortNotesHighlight = highlight.section_name === 'Short Notes';
                          const isClassNotesHighlight = highlight.section_name?.startsWith('Class Notes|');
                          if (isShortNotesHighlight) {
                            scrollToHighlightInShortNotes(highlight);
                          } else if (isClassNotesHighlight) {
                            scrollToHighlightInClassNotes(highlight);
                          } else {
                            scrollToHighlightInPDF(highlight);
                          }
                        }}
                        title={
                          highlight.section_name === 'Short Notes'
                            ? "Click to navigate to highlight in Short Notes"
                            : highlight.section_name?.startsWith('Class Notes|')
                              ? "Click to navigate to highlight in Class Notes"
                              : "Click to navigate to highlight in PDF"
                        }
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="text-sm text-muted-foreground mb-2">
                              {highlight.section_name === 'Short Notes' ? (
                                <span>Short Notes</span>
                              ) : highlight.section_name?.startsWith('Class Notes|') ? (
                                <span>Class Notes</span>
                              ) : (
                                <>
                                  Page {highlight.page_number || 'N/A'}
                                  {highlight.section_name && highlight.section_name.includes('|') && (
                                    <span> - {highlight.section_name.split('|')[1] || highlight.section_name}</span>
                                  )}
                                </>
                              )}
                            </p>
                            <div className="mt-2 p-3 bg-yellow-500/20 rounded text-sm text-foreground border-l-4 border-yellow-500">
                              <p className="font-medium text-yellow-300 mb-1">Highlighted Text:</p>
                              <p className="text-white whitespace-pre-wrap">{highlight.text}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click
                                // Jump to highlight location - check if it's from Short Notes, Class Notes, or PDF
                                const isShortNotesHighlight = highlight.section_name === 'Short Notes';
                                const isClassNotesHighlight = highlight.section_name?.startsWith('Class Notes|');
                                setShowMyHighlights(false);
                                if (isShortNotesHighlight) {
                                  scrollToHighlightInShortNotes(highlight);
                                } else if (isClassNotesHighlight) {
                                  scrollToHighlightInClassNotes(highlight);
                                } else {
                                  scrollToHighlightInPDF(highlight);
                                }
                              }}
                              title={
                                highlight.section_name === 'Short Notes'
                                  ? "Go to highlight in Short Notes"
                                  : highlight.section_name?.startsWith('Class Notes|')
                                    ? "Go to highlight in Class Notes"
                                    : "Go to highlight in PDF"
                              }
                              className="text-foreground border-[hsl(var(--card-border))] hover:bg-[hsl(var(--primary))]/20 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
                            >
                              <BookText className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async (e) => {
                                e.stopPropagation(); // Prevent card click
                                const success = await removeHighlight(highlight.id);
                                if (success) {
                                  toast.success('Highlight removed');
                                }
                              }}
                              className="text-red-400 border-red-500/50 hover:bg-red-500/20 hover:border-red-500 hover:text-red-300"
                              title="Remove highlight"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                          <span className={`w-3 h-3 rounded-full bg-${highlight.color || 'yellow'}-500`}></span>
                          <span>{highlight.created_at ? new Date(highlight.created_at).toLocaleDateString() : 'Recently'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : showNotes ? (
              shortNotesView
            ) : showClassNotes ? (
              classNotesView
            ) : null}
          </div>

          {/* Floating XP Notification */}
          {floatingXP && (
            <div className="fixed top-24 right-1/2 transform translate-x-1/2 z-50 animate-float-up">
              <div className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-bold text-2xl px-6 py-3 rounded-full shadow-2xl">
                +{floatingXP.amount} XP
              </div>
            </div>
          )}
        </div>
        {/* Global Text Actions Menu for non-PDF views */}
        {showAskBudleeAIButton && !isPdfViewActive && (
          <div
            className="fixed z-50 flex items-center gap-2"
            style={{
              top: `${buttonPosition.top}px`,
              left: `${buttonPosition.left}px`
            }}
          >
            <Button
              onClick={handleAskBudleeAIClick}
              size="sm"
              className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-[hsl(var(--main-bg))] font-bold shadow-lg animate-pulse"
            >
              <img src="/logo.png" alt="Budlee" className="w-3 h-3 mr-1 rounded-full" />
              Talk to Budlee
            </Button>
            <Button
              onClick={handleExplainClick}
              size="sm"
              className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-blue-400/20 hover:border-blue-400 hover:text-foreground shadow-lg"
              variant="outline"
            >
              <MessageCircle className="w-3 h-3 mr-1" />
              Explain
            </Button>
            <Button
              onClick={handleReadAloudClick}
              size="sm"
              className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-purple-400/20 hover:border-purple-400 hover:text-foreground shadow-lg"
              variant="outline"
            >
              <Volume2 className="w-3 h-3 mr-1" />
              Read aloud
            </Button>
            <Button
              onClick={handleHighlightClick}
              size="sm"
              variant="outline"
              className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-yellow-400/20 hover:border-yellow-400 hover:text-foreground shadow-lg"
            >
              <Highlighter className="w-3 h-3 mr-1" />
              Highlight
            </Button>

          </div>
        )}

        {/* Global Remove Highlight Button */}
        {showRemoveHighlight && !isPdfViewActive && (
          <div
            className="fixed z-50"
            style={{
              top: `${removeHighlightPosition.top}px`,
              left: `${removeHighlightPosition.left}px`
            }}
          >
            <Button
              onClick={handleRemoveHighlightClick}
              size="sm"
              variant="outline"
              className="bg-red-600/20 border-red-500/50 text-red-400 hover:bg-red-600/40 hover:border-red-500 shadow-lg"
            >
              <X className="w-3 h-3 mr-1" />
              Remove Highlight
            </Button>
          </div>
        )}
        {/* Global Text Actions Menu for non-PDF views */}
        {showAskBudleeAIButton && !isPdfViewActive && (
          <div
            className="fixed z-50 flex items-center gap-2"
            style={{
              top: `${buttonPosition.top}px`,
              left: `${buttonPosition.left}px`
            }}
          >
            <Button
              onClick={handleAskBudleeAIClick}
              size="sm"
              className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:from-[hsl(var(--teal-vivid))] hover:to-[hsl(var(--green-bright))] text-[hsl(var(--main-bg))] font-bold shadow-lg animate-pulse"
            >
              <img src="/logo.png" alt="Budlee" className="w-3 h-3 mr-1 rounded-full" />
              Talk to Budlee
            </Button>
            <Button
              onClick={handleExplainClick}
              size="sm"
              className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-blue-400/20 hover:border-blue-400 hover:text-foreground shadow-lg"
              variant="outline"
            >
              <MessageCircle className="w-3 h-3 mr-1" />
              Explain
            </Button>
            <Button
              onClick={handleReadAloudClick}
              size="sm"
              className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-purple-400/20 hover:border-purple-400 hover:text-foreground shadow-lg"
              variant="outline"
            >
              <Volume2 className="w-3 h-3 mr-1" />
              Read aloud
            </Button>
            <Button
              onClick={handleHighlightClick}
              size="sm"
              variant="outline"
              className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-yellow-400/20 hover:border-yellow-400 hover:text-foreground shadow-lg"
            >
              <Highlighter className="w-3 h-3 mr-1" />
              Highlight
            </Button>

          </div>
        )}

        {/* Global Remove Highlight Button */}
        {showRemoveHighlight && !isPdfViewActive && (
          <div
            className="fixed z-50"
            style={{
              top: `${removeHighlightPosition.top}px`,
              left: `${removeHighlightPosition.left}px`
            }}
          >
            <Button
              onClick={handleRemoveHighlightClick}
              size="sm"
              variant="outline"
              className="bg-red-600/20 border-red-500/50 text-red-400 hover:bg-red-600/40 hover:border-red-500 shadow-lg"
            >
              <X className="w-3 h-3 mr-1" />
              Remove Highlight
            </Button>
          </div>
        )}
      </div >

      {/* Activity Modal */}
      < ActivityModal
        item={selectedActivity}
        isOpen={isActivityModalOpen}
        onClose={() => {
          setIsActivityModalOpen(false);
          setSelectedActivity(null);
        }
        }
        onComplete={handleActivityComplete}
        isAlreadyCompleted={selectedActivity ? (() => {
          const activityId = selectedActivity.id || selectedActivity.annotation_id;
          const activityKey = String(activityId);
          return !!backendCompletions[activityKey] || packState.isItemCompleted(activityId);
        })() : false}
        chapterId={chapterId}
        onBookmarkChange={() => {
          console.log('🔄 TextbookViewer received onBookmarkChange, updating trigger');
          setBookmarksRefreshTrigger(prev => {
            console.log('🔄 New refresh trigger:', prev + 1);
            return prev + 1;
          });
        }}
      />

      {/* Learning Pack Viewer Modal */}
      <LearningPackViewer
        isOpen={showLearningPack}
        onClose={() => {
          setShowLearningPack(false);
          setLearningPackSectionNumber(null);
          setLearningPackSectionContext(null);
        }}
        activities={(() => {
          const normalizeSectionNumber = (value) => String(value ?? '').trim();
          const normalizeSectionTitle = (value) => {
            if (!value || typeof value !== 'string') return '';
            return value
              .replace(/\s+/g, ' ')
              .replace(/^\s*(?:section\s*)?\d+(?:\.\d+)*\s*[:\-–—.)]\s*/i, '')
              .replace(/^\s*(?:section\s*)?\d+(?:\.\d+)*\s+(?=[A-Za-z(])/i, '')
              .replace(/^\s*\d+(?:\.\d+)*\s*[:\-–—.)]\s*/i, '')
              .replace(/^\s*\d+(?:\.\d+)*\s+(?=[A-Za-z(])/i, '')
              .trim()
              .toLowerCase();
          };

          const activities = effectiveChapterAnnotations
            .filter(isLearningPackAnnotation)
            .map(ann => ({
              id: ann.id,
              annotation_id: ann.id,
              type: ann.annotation_type,
              section_number: ann.section_number ?? ann.sectionNumber ?? inferSectionNumberFromPage(ann.page_number) ?? null,
              section_name: ann.section_name ?? ann.sectionName ?? null,
              page_number: ann.page_number ?? null,
              title: ann.title || ann.annotation_type,
              description: ann.description || '',
              one_liner: ann.one_liner ?? ann.oneLiner ?? null, // Add one_liner for consistency with RightPanel
              xpReward: ann.xp_reward || 0,
              duration: ann.duration || '',
              durationSeconds: ann.duration_seconds || 0,
              video_file_url: ann.video_file_url || undefined, // Include video URL for Video type activities
              youtube_url: ann.youtube_url || undefined, // Include YouTube URL if present
              icon: ann.icon_type, // Include icon type for consistency
              chapter_id: ann.chapter?.id || ann.chapter || chapterId // Include chapter ID
            }));

          const ctx = learningPackSectionContext;
          const targetSection = learningPackSectionNumber != null ? String(learningPackSectionNumber).trim() : '';

          const sectionKey =
            ctx && typeof ctx === 'object' && typeof ctx.sectionKey === 'string' ? ctx.sectionKey : '';
          const sectionKeyName = sectionKey.startsWith('name:') ? sectionKey.slice('name:'.length) : '';
          const sectionKeyNum = sectionKey.startsWith('num:') ? sectionKey.slice('num:'.length) : '';

          // Handle merged sections: collect all groupKeys from merged subsections
          const isMerged = ctx && typeof ctx === 'object' && ctx._isMerged;
          const mergedSections = isMerged && Array.isArray(ctx._mergedSections) ? ctx._mergedSections : null;

          // 0) Best: use sectionKey from PDFSectionViewer to disambiguate same-page sections.
          if (isMerged && mergedSections) {
            // For merged sections, match activities against ANY of the merged groupKeys
            const mergedGroupKeys = mergedSections.map(s => s.groupKey).filter(Boolean);
            return activities.filter(a => {
              const activityGroupKey = a.section_name ? `name:${a.section_name}` : a.section_number ? `num:${a.section_number}` : null;
              return activityGroupKey && mergedGroupKeys.includes(activityGroupKey);
            });
          }

          if (sectionKeyName) {
            const targetName = normalizeTitle(sectionKeyName);
            return activities.filter(a => normalizeTitle(a.section_name) === targetName);
          }
          if (sectionKeyNum) {
            const target = normalizeSectionNumber(sectionKeyNum);
            return activities.filter(a => normalizeSectionNumber(a.section_number) === target);
          }

          // 1) Best: filter by page range when provided (matches how sections are defined).
          const startPageRaw = ctx && typeof ctx === 'object' ? ctx.startPage ?? ctx.start_page : null;
          const endPageRaw = ctx && typeof ctx === 'object' ? ctx.endPage ?? ctx.end_page : null;
          const startPage = Number.isFinite(parseInt(startPageRaw, 10)) ? parseInt(startPageRaw, 10) : null;
          const endPage = Number.isFinite(parseInt(endPageRaw, 10)) ? parseInt(endPageRaw, 10) : null;
          const anyHasPage = activities.some(a => Number.isFinite(parseInt(a.page_number, 10)) && parseInt(a.page_number, 10) > 0);

          if (startPage && anyHasPage) {
            return activities.filter(a => {
              const p = parseInt(a.page_number, 10);
              if (!Number.isFinite(p) || p <= 0) return false;
              if (p < startPage) return false;
              if (endPage && p >= endPage) return false;
              return true;
            });
          }

          // 2) Next: section_number exact match (no subsection roll-up).
          const anyHasSectionNumber = activities.some(a => normalizeSectionNumber(a.section_number));
          if (anyHasSectionNumber && targetSection) {
            const target = normalizeSectionNumber(targetSection);
            return activities.filter(a => {
              const sn = normalizeSectionNumber(a.section_number);
              return sn === target;
            });
          }

          // 3) Fallback: match by normalized section title.
          const ctxTitle =
            ctx && typeof ctx === 'object'
              ? (ctx.sectionTitle || ctx.section_title || ctx.title || ctx.fullTitle || '')
              : '';
          const targetTitle = normalizeSectionTitle(ctxTitle);
          const anyHasSectionName = activities.some(a => normalizeSectionTitle(a.section_name));
          if (targetTitle && anyHasSectionName) {
            return activities.filter(a => normalizeSectionTitle(a.section_name) === targetTitle);
          }

          return activities;
        })()}
        chapterId={chapterId}
        onXPEarned={onXPEarned}
      />

      {/* Floating Chat Button - Visible only when chat panel is closed */}
      {
        !chatMode && onToggleChatMode && (
          <BudleeAIIcon
            size="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16"
            iconSize="w-6 h-6 sm:w-7 h-7 lg:w-8 h-8"
            position="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 lg:bottom-4 lg:right-6"
            className="z-[100]"
            onClick={onToggleChatMode}
            showBadge={true}
          />
        )
      }
    </div >
  );
}
